import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useReducer,
  useCallback,
} from "react";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { Spinner, UncontrolledTooltip } from "reactstrap";
import { RenderCanvasElement } from "../project/style";
import { InternalsDimensionIcon } from "./style";
import {
  saveObjectPositions,
  addWall,
  copyItems,
  updateRotation,
} from "./service";
import { useParams } from "react-router-dom";
import { AppContext } from "../context";
import ConfirmationModal from "../project/ConfirmationModal";
import { ReactComponent as InternalIcon } from "../assets/img/internals.svg";
import { ReactComponent as MeasurementIcon } from "../assets/img/measurement.svg";
import { ReactComponent as ArrowHead } from "../assets/img/arrow-head.svg";
import AnnotationGenerator from "../project/helpers/annotation_generator";
import { getViewOperationSpec } from "../project/helpers/rotation_helper";
import ThreeDCanvas from "./ThreeDCanvas";
import profileGlassShutter from "../assets/img/profile_glass_shutter.png";
import gProfileTop from "../assets/img/g_profile_top.png";
import gProfileLeft from "../assets/img/g_profile_left.png";
import gProfileRight from "../assets/img/g_profile_right.png";
import gProfileBottom from "../assets/img/g_profile_bottom.png";
import golaProfileHorizontal from "../assets/img/gola_profile_horizotal.png";
import golaProfileVertical from "../assets/img/gola_profile_vertical.png";
import RightSideModal from "./RightSideModal";

function RoomCanvas({
  selectionManager,
  historyManager,
  currentRoom,
  showShutters,
  setShowShutters,
  projectId,
  loadRoom,
  isLoading,
  isReset,
  setIsReset,
  position,
  setPosition,
  zoom,
  setZoom,
  getObjectKey,
  hoverItem,
  setHoverItem,
  isEq,
  access,
  containerRef,
  setCurrentTab,
  version,
  isRoomViewInSectionCanvas = false,
  cameraView,
  setCameraView,
  onConfigureFunctionsReady,
  setCanvasFunctions = null,
  canvasContainerRef = null,
  projectSummary,
  wallView,
  setWallView,
  shouldCenterOnSelection = false,
  setShouldCenterOnSelection,
}) {
  const params = useParams();
  const { isRemarksOn, isExpoOn } = useContext(AppContext);
  const scrollSensitivity = 0.00025;
  const maxZoom = 3;
  const minZoom = 0.1;
  const panFactor = 0.1;
  const renderingSize = 5;
  const move = 10;
  const shiftMove = 100;
  const minMoveThreshold = 20;
  const zIndexMax = 1000000;

  const [initialPosition, setInitialPosition] = useState({ x: 200, y: 200 });
  const selectionManagerRef = useRef(selectionManager);
  const cameraViewRef = useRef("front");
  const [showRemovedShutters, setShowRemovedShutters] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(null);
  const [showLabels, setShowLabels] = useState(false);
  const [showModuleLabels, setShowModuleLabels] = useState(false);
  const [showColors, setShowColors] = useState(true);
  const [wallConfirmationOpen, setWallConfirmationOpen] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({});
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [rectSelector, setRectSelector] = useState({
    active: false,
    startPos: null,
    currentPos: null,
  });
  const [annotations, setAnnotations] = useState([]);
  const [show3d, setShow3d] = useState(false);
  const [showDrilling, setShowDrilling] = useState(false);
  const [showEdges, setShowEdges] = useState(true);
  const [canvasMode, setCanvasMode] = useState("");
  const [labelPositions, setLabelPositions] = useState(new Map());
  // https://legacy.reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const moveRef = useRef({
    isMouseDown: true,
    dragging: false,
    startPositions: {},
    startMousePosition: {},
  });

  const handlesImage = {
    g_profile: {
      top: gProfileTop,
      left: gProfileLeft,
      right: gProfileRight,
      bottom: gProfileBottom,
    },
    j_profile: {
      top: gProfileTop,
      left: gProfileLeft,
      right: gProfileRight,
      bottom: gProfileBottom,
    },
    gola_profile_floor: {
      top: golaProfileHorizontal,
      left: golaProfileVertical,
      right: golaProfileVertical,
      bottom: golaProfileHorizontal,
    },
  };

  const cameraViewMapper = {
    front: [0],
    right: [90],
    back: [180],
    left: [270],
    top: [0, 90, 180, 270],
  };

  const cameraViews = [
    { view: "front", label: "A" },
    { view: "right", label: "B" },
    { view: "back", label: "C" },
    { view: "left", label: "D" },
    { view: "top", label: "T" },
  ];

  const adjacentSidesMapper = {
    front: [0, 90, 270],
    right: [90, 0, 180],
    back: [180, 90, 270],
    left: [270, 0, 180],
    top: [0, 90, 180, 270],
  };

  const getRotationsForWallView = (view, wallViewValue) => {
    if (wallViewValue === "1") {
      return cameraViewMapper[view];
    } else if (wallViewValue === "3") {
      return adjacentSidesMapper[view];
    } else if (wallViewValue === "4") {
      return [0, 90, 180, 270];
    }
  };

  const shouldShowDepthMeasurement = (module) => {
    const depth = Number(module?.dimensions?.depth);
    return (
      showMeasurements == "depth" &&
      !module?.is_filler &&
      Number.isFinite(depth) &&
      depth >= 36
    );
  };

  const isTopView = useCallback(() => cameraView === "top", [cameraView]);

  const computeRotation = (rotation, cameraView) => {
    if (isTopView()) return rotation;

    const cameraRotation = cameraViewMapper[cameraView][0];
    const computedRotation = (rotation - cameraRotation + 360) % 360;

    return computedRotation;
  };

  const [showPositionModal, setShowPositionModal] = useState(false);
  const positionModalRef = useRef(null);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const togglewallConfirmationOpen = () => {
    setWallConfirmationOpen(!wallConfirmationOpen);
  };

  useEffect(() => {
    const oldModule = selectionManagerRef.current.module;
    selectionManagerRef.current = selectionManager;
    selectionManagerRef.current["module"] = oldModule;
  }, [selectionManager]);

  useEffect(() => {
    cameraViewRef.current = cameraView;
  }, [cameraView]);

  // Reset wallView to "1" when cameraView changes to top
  useEffect(() => {
    if (isTopView() && wallView !== "1") setWallView("1");
  }, [cameraView]);

  useEffect(() => {
    const module = selectionManagerRef.current.module;

    if (module) {
      const newModule = selectionManagerRef.current.findModule(module.id);
      selectionManagerRef.current.select(newModule);
    }
  }, [currentRoom]);

  // ###################################################### SELECTION LOGIC #########################################################################

  const selectItem = (item, isShiftKey) => {
    selectionManager.toggle(item, isShiftKey);
  };
  const isAnyConditionOn = isRemarksOn || isExpoOn;

  const shouldBlurModule = (module) => {
    const hasRemarks = !!module.remarks;
    const hasExposedSides = module.customizations.exposed_sides.some(
      (side) => side !== null
    );

    const remarksConditionMet = isRemarksOn && hasRemarks;
    const expoConditionMet = isExpoOn && hasExposedSides;
    const shouldBlur =
      isRemarksOn || isExpoOn
        ? !(remarksConditionMet || expoConditionMet)
        : !(hasRemarks || hasExposedSides);

    return shouldBlur;
  };

  const handleClick = (target, clickType, isShiftKey, isAltKey) => {
    if (clickType === "single") {
      let finalSelection = target;

      if (!isAltKey && target.type !== "wall") {
        let commonAncestors = [];
        selectionManager.selections().forEach((sel, index) => {
          commonAncestors.push(findCommonAncestor(sel, target));
        });
        finalSelection = getClosestOptionToTarget(target, commonAncestors);
      }

      if (finalSelection.type === "room") {
        selectionManager.clear();
        setShowPositionModal(false);
        setHoverItem(null);
      } else {
        selectItem(finalSelection, isShiftKey);
      }
    } else if (clickType === "double") {
      let finalSelection = target;
      if (!isAltKey && target.type !== "wall") {
        finalSelection = findOneStepAncestor(
          selectionManager.selection(),
          target
        );
      }
      selectItem(finalSelection, isShiftKey);
    }
  };

  function getClosestOptionToTarget(target, optionList) {
    let targetAncestry = getAncestors(target);
    let closestToTarget;
    targetAncestry.forEach((ancestor) => {
      if (optionList.find((o) => isEq(o, ancestor))) {
        closestToTarget = ancestor;
        return;
      }
    });
    return closestToTarget || targetAncestry[targetAncestry.length - 1];
  }

  const findCommonAncestor = (object1, object2) => {
    let object1Ancestors = getAncestors(object1);
    let object2Ancestors = getAncestors(object2);
    var commonItems = getCommonItems(object2Ancestors, object1Ancestors);

    if (commonItems.length === 0) {
      return object2Ancestors[object2Ancestors.length - 1];
    } else if (commonItems[0] == object1) {
      return commonItems[0];
    } else
      return object2Ancestors[object2Ancestors.length - commonItems.length - 1];
  };

  const findOneStepAncestor = (object1, object2) => {
    let object1Ancestors = getAncestors(object1);
    let object2Ancestors = getAncestors(object2);
    var commonItems = getCommonItems(object2Ancestors, object1Ancestors);
    if (commonItems.length === 0) {
      return object2Ancestors[object2Ancestors.length - 1];
    } else {
      let commonItemIndex = object2Ancestors.indexOf(commonItems[0]);
      return commonItemIndex === 0
        ? object2Ancestors[0]
        : object2Ancestors[commonItemIndex - 1];
    }
  };

  const getCommonItems = (targetItems, selectionItems) => {
    if (targetItems == null || selectionItems == null) {
      return [];
    }

    let commonItems = [];
    for (var i = 0; i < targetItems.length; i++) {
      for (var j = 0; j < selectionItems.length; j++) {
        if (isEq(targetItems[i], selectionItems[j])) {
          commonItems.push(targetItems[i]);
        }
      }
    }
    return commonItems;
  };

  const getAncestors = (object) => {
    if (!object) {
      return null;
    } else if (object.type == "room") {
      return [];
    }

    let ancestors = [];
    let currentNode = object;

    while (currentNode) {
      ancestors.push(currentNode);
      currentNode = getParent(currentNode);
    }
    return ancestors;
  };

  const getParent = (object) => {
    switch (object.type) {
      case "space":
        if (object.path === "") {
          return selectionManager.findModule(object.order_item_id);
        } else {
          let parentSpacePath = getParentSpacePath(object.path);
          return selectionManager.findSpace(
            parentSpacePath,
            object.order_item_id
          );
        }
      case "partition":
        return selectionManager.findModule(object.order_item_id);
      case "shutter":
        return selectionManager.findModule(object.order_item_id);
      case "module":
        return object.group_id
          ? selectionManager.findGroup(object.group_id)
          : null;
      case "group":
        return null;
    }
  };

  const getParentSpacePath = (path) => {
    return `${path.substring(0, path.lastIndexOf(","))}`;
  };

  // ###################################################### CANVAS RELATED FUNCTIONS/VARIABLES #########################################################################

  const [isDragging, setIsDragging] = useState(false);
  const [isCentering, setIsCentering] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const handleWheel = (event) => {
    event.preventDefault();
    setIsCentering(false);

    if (event.ctrlKey || event.metaKey) {
      const { deltaY } = event;
      setZoom((zoom) =>
        clamp(zoom + deltaY * scrollSensitivity * -1, minZoom, maxZoom)
      );
    } else if (event.shiftKey) {
      setPosition((prevPosition) => ({
        ...prevPosition,
        x: prevPosition.x - event.deltaY * panFactor,
      }));
    } else {
      setPosition((prevPosition) => ({
        x: prevPosition.x - event.deltaX * panFactor,
        y: prevPosition.y - event.deltaY * panFactor * -1,
      }));
    }
  };

  const handleMouseDown = (event) => {
    switch (event.button) {
      case 1:
        setIsDragging(true);
        setInitialPosition({ x: position.x, y: position.y });

        setDragStart({
          x: event.clientX,
          y: event.clientY,
        });
    }
  };

  const isDraggable = (item) => {
    if (!item) {
      return false;
    }

    return (
      item.type === "group" ||
      item.type === "wall" ||
      (item.type === "module" && item.is_packed === false)
    );
  };

  const handleItemSelectionForDrag = (event, object) => {
    let selections = selectionManager.selections();

    if (!selections.length) {
      // No selection yet.
      if (object.type === "wall") {
        selections = [object];
      } else {
        if (object.type === "module") {
          selections = [object];
        } else {
          if (object.order_item_id) {
            const module = selectionManager.findModule(object.order_item_id);

            if (module.group_id) {
              selections = [selectionManager.findGroup(module.group_id)];
            } else {
              selections = [module];
            }
          } else {
            console.log("Module context missing. Ignoring");
            return;
          }
        }
      }
    }

    moveRef.current.startMousePosition = {
      x: event.clientX,
      y: event.clientY,
    };
    moveRef.current.startPositions = {};

    for (let item of selections) {
      if (Array.isArray(item)) {
        item = item[0];
      }
      if (!isDraggable(item)) {
        continue;
      }
      const key = getObjectKey(item);
      moveRef.current.startPositions[key] = {
        x: item.position.x,
        y: item.position.y,
        z: item.position.z,
      };

      if (item.type === "group") {
        item.modules.forEach((m) => {
          const key = getObjectKey(m);
          const position = {
            x: m.position.x,
            y: m.position.y,
            z: m.position.z,
          };
          moveRef.current.startPositions[key] = position;
        });
      } else {
        // FIXME? Group update?
      }
    }

    moveRef.current.isMouseDown = true;
  };

  const handleItemClick = (event, object) => {
    if (event.button === 0) {
      if (object.type === "room") {
        if (!event.shiftKey) {
          selectionManager.clear();
          setShowPositionModal(false);
          setHoverItem(null);
        }

        setRectSelector({
          active: true,
          startPos: { x: event.clientX, y: event.clientY },
          currentPos: { x: event.clientX, y: event.clientY },
        });

        return;
      }

      if (!["shutter", "space", "partition", "wall"].includes(object.type)) {
        object = selectionManager.findModule(object.order_item_id);
      }

      switch (event.detail) {
        case 1: {
          if (access.customize_module) {
            handleItemSelectionForDrag(event, object);
          }

          timer.current = setTimeout(() => {
            handleClick(object, "single", event.shiftKey, event.altKey);
          }, 200);
          break;
        }
        case 2: {
          clearTimeout(timer.current);
          handleClick(object, "double", event.shiftKey, event.altKey);
          break;
        }
        default: {
          break;
        }
      }
    }

    event.stopPropagation();
  };

  const handleMouseMove = (event) => {
    if (isDragging) {
      setPosition({
        x: initialPosition.x + event.clientX - dragStart.x,
        y: initialPosition.y + dragStart.y - event.clientY,
      });
    }

    const selectedItems = selectionManager.selections();

    if (moveRef.current.isMouseDown) {
      moveRef.current.dragging = true;
    }

    if (moveRef.current.dragging) {
      const deltaX = event.clientX - moveRef.current.startMousePosition.x;
      const deltaY = event.clientY - moveRef.current.startMousePosition.y;

      if (
        Math.abs(deltaX) > minMoveThreshold ||
        Math.abs(deltaY) > minMoveThreshold
      ) {
        const spec = getViewOperationSpec(cameraViewRef.current);
        const horizontalDelta =
          (spec.horizontalSign * deltaX * renderingSize) / zoom;
        // Screen Y is inverted: positive deltaY means moving down, so we negate it
        const verticalDelta =
          (spec.verticalSign * -deltaY * renderingSize) / zoom;

        for (let item of selectedItems) {
          const key = getObjectKey(item);

          if (moveRef.current.startPositions[key]) {
            // Update horizontal axis
            item.position[spec.horizontalAxis] =
              moveRef.current.startPositions[key][spec.horizontalAxis] +
              horizontalDelta;

            // Update vertical axis
            item.position[spec.verticalAxis] =
              moveRef.current.startPositions[key][spec.verticalAxis] +
              verticalDelta;

            if (item.type === "group") {
              item.modules.forEach((m) => {
                const moduleKey = getObjectKey(m);
                m.position[spec.horizontalAxis] =
                  moveRef.current.startPositions[moduleKey][
                    spec.horizontalAxis
                  ] + horizontalDelta;
                m.position[spec.verticalAxis] =
                  moveRef.current.startPositions[moduleKey][spec.verticalAxis] +
                  verticalDelta;
              });
            } else if (item.type === "module" && item.group_id) {
              const group = selectionManager.findGroup(item.group_id);
              if (group.category === "BasicGroup") {
                const children = (group.modules || []).concat(
                  group.walls || []
                );
                group.position =
                  selectionManager.calculateGroupPosition(children);
                group.dimensions =
                  selectionManager.calculateGroupDimensions(children);
              }
            }
          }
        }

        moveRef.current.isMoved = true;

        forceUpdate();
      }
    }

    if (rectSelector.active) {
      const currentPos = { x: event.clientX, y: event.clientY };
      setRectSelector({ ...rectSelector, currentPos: currentPos });
      // DOM-based selection: compare drag-rect (client pixels) with each node's DOMRect.
      const xMin = Math.min(rectSelector.startPos.x, currentPos.x);
      const xMax = Math.max(rectSelector.startPos.x, currentPos.x);
      const yMin = Math.min(rectSelector.startPos.y, currentPos.y);
      const yMax = Math.max(rectSelector.startPos.y, currentPos.y);
      selectObjectsInBounds({ xMin, xMax, yMin, yMax });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    moveRef.current.isMouseDown = false;

    if (moveRef.current.dragging) {
      if (moveRef.current.isMoved) {
        const selectedItems = selectionManager
          .selections()
          .filter((item) => isDraggable(item));

        if (selectedItems.length > 0) {
          moveObjects(selectedItems, moveRef.current.startPositions);
        }
      }

      moveRef.current.dragging = false;
      moveRef.current.startPositions = {};
      moveRef.current.startMousePosition = null;
      moveRef.current.isMoved = false;
    } else if (rectSelector.active) {
      setRectSelector({ ...rectSelector, active: false });
    }
  };

  const moveItemsByKeys = (event) => {
    event.preventDefault();

    const selectedItems = selectionManagerRef.current
      .selections()
      .filter((item) => isDraggable(item));
    const oldPositions = {};

    if (selectedItems.length > 0) {
      const spec = getViewOperationSpec(cameraViewRef.current);
      const moveAmount = event.shiftKey ? shiftMove : move;

      // Map arrow keys to movement deltas
      let horizontalDelta = 0;
      let verticalDelta = 0;

      if (event.key === "ArrowLeft") {
        horizontalDelta = -moveAmount * spec.horizontalSign;
      } else if (event.key === "ArrowRight") {
        horizontalDelta = moveAmount * spec.horizontalSign;
      } else if (event.key === "ArrowUp") {
        verticalDelta = moveAmount * spec.verticalSign;
      } else if (event.key === "ArrowDown") {
        verticalDelta = -moveAmount * spec.verticalSign;
      }

      for (let item of selectedItems) {
        oldPositions[getObjectKey(item)] = {
          x: item.position.x,
          y: item.position.y,
          z: item.position.z,
        };

        if (horizontalDelta !== 0) {
          item.position[spec.horizontalAxis] += horizontalDelta;
        }
        if (verticalDelta !== 0) {
          item.position[spec.verticalAxis] += verticalDelta;
        }

        if (item.type === "group") {
          item.modules.forEach((m) => {
            oldPositions[getObjectKey(m)] = {
              x: m.position.x,
              y: m.position.y,
              z: m.position.z,
            };

            if (horizontalDelta !== 0) {
              m.position[spec.horizontalAxis] += horizontalDelta;
            }
            if (verticalDelta !== 0) {
              m.position[spec.verticalAxis] += verticalDelta;
            }
          });
        } else if (item.type === "module" && item.group_id) {
          const group = selectionManager.findGroup(item.group_id);
          if (group && group.category === "BasicGroup") {
            const children = (group.modules || []).concat(group.walls || []);
            group.position = selectionManager.calculateGroupPosition(children);
            group.dimensions =
              selectionManager.calculateGroupDimensions(children);
          }
        }
      }

      forceUpdate();
      moveObjects(selectedItems, oldPositions);
    }
  };

  let latestMoveTimestamps = {};

  const onMove = (newItems) => {
    const t = Date.now();
    const selectionKey = selectionManager.getKey();
    latestMoveTimestamps[selectionKey] = t;

    setTimeout(
      ((key) => {
        return () => {
          if (latestMoveTimestamps[key] === t) {
            saveObjectPositions({
              projectId: params.projectId,
              roomId: selectionManagerRef.current.currentRoom().id,
              items: newItems.map(({ position, type, id }) => ({
                position,
                type,
                id,
              })),
            }).catch(({ error }) => {
              setIsNotificationVisible(true);
              setNotificationMessage({
                text: "Error updating group position: " + error,
                type: "danger",
              });
            });
          }
        };
      })(selectionKey),
      500
    );
  };

  const moveObjects = (items, oldPositions) => {
    const itemsCopy = [...items];
    const newPositions = {};

    items.forEach((item) => {
      newPositions[getObjectKey(item)] = { ...item.position };
    });

    const groups = itemsCopy.filter((i) => i.type === "group");
    const modules = groups.map((e) => e.modules).flat();

    groups.forEach((g) => {
      const index = itemsCopy.findIndex((i) => i.id == g.id);
      itemsCopy.splice(index, 1);
    });

    const newItems = itemsCopy.concat(modules);
    historyManager.add("move", oldPositions, newPositions);
    onMove(newItems);
  };

  function getAllObjects({
    modules = true,
    groups = true,
    walls = true,
    groupChildren = false,
  }) {
    let objects = [];
    const allowedRotations = getRotationsForWallView(cameraView, wallView);

    if (modules)
      objects = objects.concat(
        currentRoom.modules.filter((module) =>
          allowedRotations.includes(module.rotation)
        )
      );

    if (groups)
      objects = objects.concat(
        currentRoom.groups.filter((group) =>
          allowedRotations.includes(group.rotation)
        )
      );

    if (walls) objects = objects.concat(currentRoom.walls);

    if (groupChildren) {
      const children = currentRoom.groups
        .map((group) =>
          group.modules
            .filter(() => allowedRotations.includes(group.rotation))
            .concat(group.walls)
        )
        .flat();
      objects = objects.concat(children);
    }

    return objects;
  }

  function selectObjectsInBounds(selectionBounds) {
    const allObjects = getAllObjects({
      modules: true,
      groups: true,
      walls: true,
    });
    const toBeSelected = [];

    for (var i = 0; i < allObjects.length; i++) {
      const item = allObjects[i];
      const key = getObjectKey(item);
      const el = containerRef.current?.querySelector(
        `[data-object-key="${key}"]`
      );

      if (!el) continue;

      const r = el.getBoundingClientRect();
      const touches =
        selectionBounds.xMax >= r.left &&
        selectionBounds.xMin <= r.right &&
        selectionBounds.yMax >= r.top &&
        selectionBounds.yMin <= r.bottom;

      if (touches) toBeSelected.push(item);
    }

    selectionManager.selectMultiple(toBeSelected);
  }

  const handleMeasurementClick = () => {
    if (showMeasurements === null) {
      setShowMeasurements("hw");
    } else if (showMeasurements === "hw") {
      setShowMeasurements("depth");
    } else {
      setShowMeasurements(null);
    }
  };

  const getMeasurementText = () => {
    switch (showMeasurements) {
      case "hw":
        return "H & W";
      case "depth":
        return "Depth";
      default:
        return null;
    }
  };

  const handleLabelClick = () => {
    if (!showModuleLabels) {
      setShowModuleLabels("all");
    } else if (showModuleLabels === "all") {
      setShowModuleLabels("shutters");
    } else if (showModuleLabels === "shutters" && !showShutters) {
      setShowModuleLabels("spaces");
    } else {
      setShowModuleLabels(null);
    }
  };

  const getLabelText = () => {
    switch (showModuleLabels) {
      case "all":
        return "All";
      case "shutters":
        return "Panel";
      case "spaces":
        return "Space";
      default:
        return null;
    }
  };

  const formatLeafSpacePath = (path) => path.replace(/,/g, ".");

  const Arrow = ({ direction }) => {
    const rotation = {
      up: 0,
      right: 90,
      down: 180,
      left: 270,
    }[direction];

    return (
      <svg width="12" height="12" viewBox="0 0 12 12">
        <g transform={`rotate(${rotation}, 6, 6)`}>
          <ArrowHead />
        </g>
      </svg>
    );
  };

  const V = (axis, sign = +1) => ({ axis, sign });

  // View transformations
  // x0 = front view (no X rotation), x90 = top view (90° X rotation)
  // y0, y90, y180, y270 = Y-axis rotations (module rotation)
  const VIEW_MAP = {
    // Front view (x0)
    x0_y0: { u: V("x", +1), v: V("y", +1), w: V("z", +1) }, // front
    x0_y90: { u: V("z", -1), v: V("y", +1), w: V("x", +1) }, // right
    x0_y180: { u: V("x", -1), v: V("y", +1), w: V("z", -1) }, // back
    x0_y270: { u: V("z", +1), v: V("y", +1), w: V("x", -1) }, // left
    // Top view (x90) - looking from top, depth becomes height
    // When looking from top: u=x (width), v=z (depth->height), w=-y (height->depth, flipped)
    x90_y0: { u: V("x", +1), v: V("z", -1), w: V("y", -1) }, // top, front orientation
    x90_y90: { u: V("z", -1), v: V("x", -1), w: V("y", -1) }, // top, right orientation
    x90_y180: { u: V("x", -1), v: V("z", +1), w: V("y", -1) }, // top, back orientation
    x90_y270: { u: V("z", +1), v: V("x", +1), w: V("y", -1) }, // top, left orientation
  };

  // Helper to generate view key based on camera view and rotation
  function viewKey(rotYDeg) {
    const ry = ((rotYDeg % 360) + 360) % 360;
    const xRot = isTopView() ? "x90" : "x0";
    return `${xRot}_y${ry}`;
  }

  const SIZE_MAP = {
    widthAxis: (spec) => spec.u.axis,
    heightAxis: (spec) => spec.v.axis,
  };

  function dimOnAxis(dimensions, axis) {
    if (axis === "x") return dimensions.width;
    if (axis === "y") return dimensions.height;

    return dimensions.depth; // 'z'
  }

  function computeSize(object, spec, renderingSize) {
    const widthAxis = SIZE_MAP.widthAxis(spec);
    const heightAxis = SIZE_MAP.heightAxis(spec);

    const width = dimOnAxis(object.dimensions, widthAxis) / renderingSize;
    const height = dimOnAxis(object.dimensions, heightAxis) / renderingSize;

    return { width: `${width}px`, height: `${height}px` };
  }

  function posOnAxis(position, axis) {
    if (axis === "x") return position.x;
    if (axis === "y") return position.y;

    return position.z; // 'z'
  }

  function componentSizeOnAxis(component, axis) {
    if (axis === "x") return component.dimensions.width;
    if (axis === "y") return component.dimensions.height;

    return component.dimensions.depth; // 'z'
  }

  const POSITION_MAP = {
    left: (object, container, spec) =>
      signedMinOrFlipped(object, container, spec.u),
    top: (object, container, spec) =>
      signedMinOrFlipped(object, container, spec.v),
  };

  function signedMinOrFlipped(object, container, signedAxis) {
    const { axis, sign } = signedAxis;
    const min = posOnAxis(object.position, axis);
    const max = min + dimOnAxis(object.dimensions, axis);
    const compSize = componentSizeOnAxis(container, axis);

    return sign > 0 ? min : compSize - max;
  }

  function computePosition(object, container, spec, renderingSize) {
    const left = POSITION_MAP.left(object, container, spec) / renderingSize;
    // Calculate bottom position based on v axis (vertical screen axis)
    // For front view: v = y, for top view: v = z
    const bottom = POSITION_MAP.top(object, container, spec) / renderingSize;
    return { left: `${left}px`, bottom: `${bottom}px` };
  }

  const ZINDEX_MAP = {
    near: (object, container, spec) => {
      const axis = spec.w.axis;
      const min = posOnAxis(object.position, axis);
      const max = min + dimOnAxis(object.dimensions, axis);
      const compSize = componentSizeOnAxis(container, axis);

      if (spec.w.sign > 0) {
        // Positive direction: use max (farther = higher zIndex)
        return max;
      } else if (axis === "y") {
        // Negative direction: for Y axis (top view), higher Y = closer = higher zIndex
        // For other axes, flip using component size
        // Looking from top: higher Y position = closer = higher zIndex
        return max;
      } else {
        // For X and Z axes: flip using component size (closer = higher zIndex)
        return compSize - max;
      }
    },
  };

  function computeZIndex(object, container, spec) {
    return Math.round(ZINDEX_MAP.near(object, container, spec));
  }

  // Get which laminate to show based on the view direction (w axis)
  // The w axis tells us which direction we're looking from
  function getVisibleLaminate(panel, spec) {
    // Map w axis directly to laminate property
    // w = V("z", +1) → front view → front_laminate
    // w = V("x", +1) → right view → right_laminate
    // w = V("z", -1) → back view → back_laminate
    // w = V("x", -1) → left view → left_laminate
    // w = V("y", -1) → top view (looking from negative Y) → top_laminate
    // w = V("y", +1) → bottom view (looking from positive Y) → bottom_laminate

    if (spec.w.axis === "z") {
      return spec.w.sign > 0 ? panel.front_laminate : panel.back_laminate;
    } else if (spec.w.axis === "x") {
      return spec.w.sign > 0 ? panel.right_laminate : panel.left_laminate;
    } else if (spec.w.axis === "y") {
      return spec.w.sign < 0 ? panel.top_laminate : panel.bottom_laminate;
    }
  }

  function applyPanelTransform(
    panel,
    component,
    { rotYDeg, cameraView },
    renderingSize
  ) {
    const spec = VIEW_MAP[viewKey(rotYDeg, cameraView)];
    const size = computeSize(panel, spec, renderingSize);
    const pos = computePosition(panel, component, spec, renderingSize);
    const zIndex = computeZIndex(panel, component, spec);

    return {
      style: { ...size, ...pos },
      zIndex,
      spec,
    };
  }

  // Generic transformation for components and spaces
  function applyTransform(
    object,
    container,
    { rotYDeg, cameraView },
    renderingSize
  ) {
    const spec = VIEW_MAP[viewKey(rotYDeg, cameraView)];
    const size = computeSize(object, spec, renderingSize);
    const pos = computePosition(object, container, spec, renderingSize);
    const zIndex = computeZIndex(object, container, spec);

    return {
      style: { ...size, ...pos },
      zIndex,
      spec,
    };
  }

  function getGroupFrontZ(group) {
    return group.position.z + group.dimensions.depth;
  }

  function getModuleFrontZ(module) {
    return module.position.z + module.dimensions.depth;
  }

  function getGroupX2(group) {
    return group.position.x + group.dimensions.width;
  }

  function getModuleX2(module) {
    return module.position.x + module.dimensions.width;
  }

  const MODULE_POSITION_SPEC_MATRIX = {
    front: {
      0: {
        position: null, // No position change
        zIndex: {
          sign: "+",
          terms: ["module.z", "module.depth"],
        },
      },
      90: {
        position: {
          base: "group.x",
          offset: "frontZDiff",
          side: "left",
        },
        zIndex: {
          sign: "+",
          terms: ["group.z", "module.width", "xDiff"],
        },
      },
      180: {
        position: {
          base: "group.x",
          offset: "x2Diff",
          side: "left",
        },
        zIndex: {
          sign: "+",
          terms: ["group.z", "group.depth", "negXDiff"],
        },
      },
      270: {
        position: {
          base: "group.x",
          offset: "zDiff",
          side: "left",
        },
        zIndex: {
          sign: "+",
          terms: ["group.z", "module.width", "x2Diff"],
        },
      },
    },
    right: {
      0: {
        position: {
          base: "group.z",
          offset: "xDiff",
          side: "left",
        },
        zIndex: {
          sign: "-",
          terms: ["group.x", "frontZDiff"],
        },
      },
      90: {
        position: {
          base: "group.z",
          offset: "frontZDiff",
          side: "left",
        },
        zIndex: {
          sign: "-",
          terms: ["group.x", "x2Diff"],
        },
      },
      180: {
        position: {
          base: "group.z",
          offset: "x2Diff",
          side: "left",
        },
        zIndex: {
          sign: "-",
          terms: ["group.x", "zDiff"],
        },
      },
      270: {
        position: {
          base: "module.z",
          offset: null,
          side: "left",
        },
        zIndex: {
          sign: "-",
          terms: ["module.x"],
        },
      },
    },
    left: {
      0: {
        position: {
          base: "group.z",
          offset: "x2Diff",
          side: "right",
        },
        zIndex: {
          sign: "+",
          terms: ["group.x", "zDiff", "module.depth"],
        },
      },
      90: {
        position: {
          base: "module.z",
          offset: null,
          side: "right",
        },
        zIndex: {
          sign: "+",
          terms: ["module.x", "module.width"],
        },
      },
      180: {
        position: {
          base: "group.z",
          offset: "negXDiff",
          offsetSign: "-",
          side: "right",
        },
        zIndex: {
          sign: "+",
          terms: ["group.x", "frontZDiff"],
        },
      },
      270: {
        position: {
          base: "group.z",
          offset: "frontZDiff",
          side: "right",
        },
        zIndex: {
          sign: "+",
          terms: ["group.x", "x2Diff", "module.width"],
        },
      },
    },
    back: {
      0: {
        position: {
          base: "group.x",
          offset: "x2Diff",
          side: "right",
        },
        zIndex: {
          sign: "-",
          terms: ["frontZDiff"],
        },
      },
      90: {
        position: {
          base: "group.x",
          offset: "zDiff",
          side: "right",
        },
        zIndex: {
          sign: "-",
          terms: ["group.z", "x2Diff"],
        },
      },
      180: {
        position: {
          base: "group.x",
          offset: "xDiff",
          side: "right",
        },
        zIndex: {
          sign: "-",
          terms: ["module.z"],
        },
      },
      270: {
        position: {
          base: "group.x",
          offset: "frontZDiff",
          side: "right",
        },
        zIndex: {
          sign: "-",
          terms: ["group.z", "xDiff"],
        },
      },
    },
  };

  const NON_GROUPED_MODULE_POSITION_SPEC_MATRIX = {
    front: {
      zIndex: {
        sign: "+",
        terms: ["module.z", "module.depth"],
      },
    },
    right: {
      position: {
        base: "module.z",
        offset: null,
        side: "left",
      },
      zIndex: {
        sign: "-",
        terms: ["module.x"],
      },
    },
    left: {
      position: {
        base: "module.z",
        offset: null,
        side: "right",
      },
      zIndex: {
        sign: "+",
        terms: ["module.x", "module.depth"],
      },
    },
    back: {
      position: {
        base: "module.x",
        offset: null,
        side: "right",
      },
      zIndex: {
        sign: "-",
        terms: ["module.z", "module.depth"],
      },
    },
  };

  function getModulePositionSpec(cameraView, computedRotation, hasGroup) {
    if (hasGroup)
      return MODULE_POSITION_SPEC_MATRIX[cameraView][computedRotation];
    else return NON_GROUPED_MODULE_POSITION_SPEC_MATRIX[cameraView];
  }

  function computeOffset(group, module, offsetType) {
    if (!offsetType || !group) return 0;

    switch (offsetType) {
      case "frontZDiff":
        return getGroupFrontZ(group) - getModuleFrontZ(module);
      case "x2Diff":
        return getGroupX2(group) - getModuleX2(module);
      case "zDiff":
        return module.position.z - group.position.z;
      case "xDiff":
        return module.position.x - group.position.x;
      case "negXDiff":
        return group.position.x - module.position.x;
      default:
        return 0;
    }
  }

  function getPositionBase(group, module, baseType) {
    switch (baseType) {
      case "group.x":
        return group ? group.position.x : 0;
      case "group.z":
        return group ? group.position.z : 0;
      case "module.x":
        return module.position.x;
      case "module.z":
        return module.position.z;
      default:
        return 0;
    }
  }

  function computeZIndexTerm(group, module, termType) {
    switch (termType) {
      case "module.z":
        return module.position.z;
      case "module.x":
        return module.position.x;
      case "module.depth":
        return module.dimensions.depth;
      case "module.width":
        return module.dimensions.width;
      case "group.z":
        return group ? group.position.z : 0;
      case "group.x":
        return group ? group.position.x : 0;
      case "group.depth":
        return group ? group.dimensions.depth : 0;
      case "xDiff":
        return group ? module.position.x - group.position.x : module.position.x;
      case "negXDiff":
        return group
          ? group.position.x - module.position.x
          : -module.position.x;
      case "zDiff":
        return group ? module.position.z - group.position.z : module.position.z;
      case "x2Diff":
        return group ? getGroupX2(group) - getModuleX2(module) : 0;
      case "frontZDiff":
        return group ? getGroupFrontZ(group) - getModuleFrontZ(module) : 0;
      default:
        return 0;
    }
  }

  function computeModulePosition(group, module, spec, renderingSize) {
    if (!spec || !spec.position) return {};

    const { base, offset, offsetSign = "+", side } = spec.position;
    const baseValue = getPositionBase(group, module, base);
    const offsetValue = computeOffset(group, module, offset);

    const positionValue =
      offsetSign === "-"
        ? (baseValue - offsetValue) / renderingSize
        : (baseValue + offsetValue) / renderingSize;

    if (side === "right") {
      return {
        left: "auto",
        right: `${positionValue}px`,
      };
    } else {
      return {
        left: `${positionValue}px`,
      };
    }
  }

  function computeModuleZIndex(group, module, spec, zIndexMax) {
    if (!spec || !spec.zIndex) return zIndexMax;

    const { sign, terms } = spec.zIndex;
    let result = zIndexMax;

    for (const term of terms) {
      const termValue = computeZIndexTerm(group, module, term);

      if (sign === "+") result += termValue;
      else result -= termValue;
    }

    return result;
  }

  const handleRotateModule = () => {
    const selectedItems = selectionManagerRef.current.selections();
    const moduleSelections = selectedItems.filter(
      (item) => item.type === "module" && !item.is_packed
    );
    const groupSelections = selectedItems.filter(
      (item) => item.type === "group"
    );

    if (moduleSelections.length === 0 && groupSelections.length === 0) return;

    // Store original rotations for error rollback
    const originalRotations = {};
    const rotationUpdates = [];

    // Handle module rotations
    moduleSelections.forEach((selectedModule) => {
      const moduleToRotate = selectionManagerRef.current.findModule(
        selectedModule.id
      );

      const currentRotation = moduleToRotate.rotation;
      const newRotation = (currentRotation + 90) % 360;

      originalRotations[`module_${moduleToRotate.id}`] = currentRotation;
      moduleToRotate.rotation = newRotation;

      rotationUpdates.push({
        id: moduleToRotate.id,
        type: "module",
        rotation: newRotation,
      });
    });

    // Handle group/section rotations
    groupSelections.forEach((selectedGroup) => {
      const groupToRotate = selectionManagerRef.current.findGroup(
        selectedGroup.id
      );

      const currentRotation = groupToRotate.rotation;
      const newRotation = (currentRotation + 90) % 360;

      originalRotations[`group_${groupToRotate.id}`] = currentRotation;
      groupToRotate.rotation = newRotation;

      rotationUpdates.push({
        id: groupToRotate.id,
        type: "group",
        rotation: newRotation,
      });
    });

    forceUpdate();

    // Regenerate measurements if they're currently showing
    if (showMeasurements === "hw") {
      generateMeasurements();
    }

    updateRotation({
      projectId: params.projectId,
      roomId: currentRoom.id,
      items: rotationUpdates,
    }).catch(({ error }) => {
      // Revert all rotations on error
      moduleSelections.forEach((selectedModule) => {
        const moduleToRotate = selectionManagerRef.current.findModule(
          selectedModule.id
        );
        moduleToRotate.rotation =
          originalRotations[`module_${moduleToRotate.id}`];
      });
      groupSelections.forEach((selectedGroup) => {
        const groupToRotate = selectionManagerRef.current.findGroup(
          selectedGroup.id
        );
        groupToRotate.rotation = originalRotations[`group_${groupToRotate.id}`];
      });
      forceUpdate();

      // Regenerate measurements if they're currently showing (rotations were reverted)
      if (showMeasurements === "hw") {
        generateMeasurements();
      }

      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: `Error updating rotations: ${error}`,
      });
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseup", handleMouseUp);
      container.addEventListener("mousedown", handleMouseDown);
      container.addEventListener("mouseleave", handleMouseUp);
    }

    return () => {
      if (container) {
        container.removeEventListener("wheel", handleWheel);
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("mouseup", handleMouseUp);
        container.removeEventListener("mousedown", handleMouseDown);
        container.removeEventListener("mouseleave", handleMouseUp);
      }
    };
  }, [handleWheel, handleMouseMove, handleMouseUp, handleMouseDown]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleKeyDown = (event) => {
    // User is possibly working on an input element. Don't apply shortcut.
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    )
      return;

    if (event.key === "Escape") {
      selectionManager.clear();
      setShowPositionModal(false);
      setHoverItem(null);
    } else if (event.key === "i" || event.key === "I") {
      toggleInternals();
    } else if (event.key === "m" || event.key === "M") {
      setShowMeasurements((prevState) => {
        if (prevState === null) {
          return "hw";
        } else if (prevState === "hw") {
          return "depth";
        } else if (prevState === "depth") {
          return null;
        }
      });
    } else if (
      (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
      access.customize_module
    ) {
      moveItemsByKeys(event);
    } else if (
      (event.key === "ArrowUp" || event.key === "ArrowDown") &&
      access.customize_module
    ) {
      moveItemsByKeys(event);
    } else if (
      event.key === "c" &&
      (event.ctrlKey || event.metaKey) &&
      version == 1
    ) {
      handleCopy();
    } else if (
      event.key === "x" &&
      (event.ctrlKey || event.metaKey) &&
      access.customize_module &&
      version == 1
    ) {
      handleCopy({ isCut: true });
    } else if (
      event.key === "v" &&
      (event.ctrlKey || event.metaKey) &&
      access.customize_module &&
      version == 1
    ) {
      handlePaste(event);
    }
  };

  const handleCopy = (isCut = false) => {
    const selections = selectionManagerRef.current.selections();

    if (
      selections.length > 0 &&
      selections.every((selection) =>
        ["wall", "module", "group"].includes(selection.type)
      )
    ) {
      localStorage.setItem(
        "copiedObjects",
        JSON.stringify({
          operation: isCut ? "cut" : "copy",
          items: selections.map(({ type, id }) => ({ type, id })),
        })
      );
    }
  };

  const handlePaste = (event) => {
    const text = localStorage.getItem("copiedObjects");
    let clipboardSelections;
    try {
      clipboardSelections = JSON.parse(text);
    } catch (error) {
      return;
    }

    if (
      clipboardSelections &&
      clipboardSelections.items &&
      clipboardSelections.items.length > 0 &&
      ["cut", "copy"].includes(clipboardSelections.operation)
    ) {
      // TODO: Cut+Paste behaves like Copy+Paste for now.
      setCanvasLoading(true);

      // COPY ITEMS
      copyItems({
        projectId: params.projectId,
        roomId: selectionManagerRef.current.currentRoom().id,
        items: clipboardSelections.items,
        target: selectionManagerRef.current.activeGroup()
          ? {
              type: selectionManagerRef.current.activeGroup().type,
              id: selectionManagerRef.current.activeGroup().id,
            }
          : null,
      })
        .then((response) => {
          // Setting position for groups
          response.copied_objects.forEach((item) => {
            if (item.type === "group") {
              item.position = selectionManager.calculateGroupPosition(
                (item.modules || []).concat(item.walls || [])
              );
            }
          });

          selectionManager.triggerLoadRoom(
            selectionManagerRef.current.currentRoom().id,
            null,
            response.copied_objects
          );
        })
        .catch(({ error }) => {
          setIsNotificationVisible(true);
          setNotificationMessage({
            text: "Error Pasting items: " + error,
            type: "danger",
          });
        })
        .finally(() => {
          setCanvasLoading(false);
        });
    }
  };

  const isSelected = (object) => {
    return selectionManager.isSelected(object);
  };

  const isRemovableSpace = (object) => {
    if (object.type !== "space") return;

    return object.path === selectionManager.selection().replaceable_space_path;
  };

  // ######################################################################### RENDERING FUNCTIONS/VARIABLES #########################################################################
  const renderLabels = (component) => {
    let renderStyle = {
      position: "absolute",
      bottom: `${
        component.type === "v_partition"
          ? component.dimensions.height / renderingSize / 2
          : component.type === "space" && component.is_drawer
          ? component.dimensions.height / renderingSize / 5
          : component.type === "shutter"
          ? component.dimensions.height / renderingSize / 2
          : component.dimensions.height / renderingSize
      }px`,
      left: `${
        component.type === "v_partition"
          ? "0"
          : component.type === "carcass"
          ? component.dimensions.width / renderingSize / 2
          : component.dimensions.width / renderingSize / 3
      }px`,
      alignContent: "center",
      textAlign: "center",
      opacity: 1,
      writingMode: `${
        component.type === "v_partition" ? "vertical-lr" : "horizontal-tb"
      }`,
      fontSize: "16px",
      zIndex: zIndexMax + 10000,
    };

    return (
      <div className="text-primary fw-bold" style={renderStyle}>
        {component.label}
      </div>
    );
  };

  const labels = (module, group = null) => {
    let renderStyle = {
      position: "absolute",
      bottom: `${module.dimensions.height / renderingSize / 2}px`,
      left: `${module.dimensions.width / renderingSize / 2.5}px`,
      color: "white",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      opacity: 1,
      fontSize: "26px",
      height: "40px",
      width: "40px",
      borderRadius: "50%",
      backgroundColor: "#000",
      zIndex: zIndexMax + 10000,
      pointerEvents: "none",
    };

    return (
      <div className="fw-bold" style={renderStyle}>
        {module.label}
      </div>
    );
  };

  const renderAllLabels = () => {
    if (showModuleLabels !== "all") {
      return null;
    }

    const allowedRotations = getRotationsForWallView(cameraView, wallView);
    const allModules = [
      ...currentRoom.groups
        .filter((group) => allowedRotations.includes(group.rotation))
        .flatMap(group =>
          group.modules
            ?.filter(() => allowedRotations.includes(group.rotation))
            .map(m => ({ module: m, group })) || []
        ),
      ...(currentRoom.modules
        ?.filter((module) => allowedRotations.includes(module.rotation))
        .map(m => ({ module: m, group: null })) || [])
    ];

    const getModulePos = (module, group) => {
      let rotation = module.rotation;
      if (rotation === 0 && group) rotation = group.rotation;
      const computedRotation = computeRotation(rotation, cameraView);

      if (isTopView()) {
        let x = module.position.x / renderingSize;
        let z = (module.position.z || 0) / renderingSize;

        if (group) {
          if (rotation === 90) {
            const groupFrontZ = group.position.z + group.dimensions.depth;
            const moduleFrontZ = module.position.z + module.dimensions.depth;
            x = (group.position.x + (groupFrontZ - moduleFrontZ)) / renderingSize;
            z = (group.position.z - (group.position.x - module.position.x)) / renderingSize;
          } else if (rotation === 180) {
            const groupX2 = group.position.x + group.dimensions.width;
            const moduleX2 = module.position.x + module.dimensions.width;
            x = (group.position.x + (groupX2 - moduleX2)) / renderingSize;
            const groupFrontZ = group.position.z + group.dimensions.depth;
            const moduleFrontZ = module.position.z + module.dimensions.depth;
            z = (group.position.z + (groupFrontZ - moduleFrontZ)) / renderingSize;
          } else if (rotation === 270) {
            x = (group.position.x - (group.position.z - module.position.z)) / renderingSize;
            const groupX2 = group.position.x + group.dimensions.width;
            const moduleX2 = module.position.x + module.dimensions.width;
            z = (group.position.z + (groupX2 - moduleX2)) / renderingSize;
          }
        }

        const adjusted = labelPositions.get(module.id);
        const centerX = adjusted ? adjusted.centerX : (x + (module.dimensions.width / renderingSize / 2));
        const centerY = adjusted ? adjusted.centerY : (z + (module.dimensions.depth / renderingSize / 2));

        return {
          left: `${centerX - 20}px`,
          top: `${centerY - 20}px`,
        };
      }

      const spec = getModulePositionSpec(cameraView, computedRotation, !!group);
      const position = computeModulePosition(group, module, spec, renderingSize);
      const pixelParser = (str) => parseFloat(str.replace("px", "")) || 0;

      const width = [90, 270].includes(computedRotation)
        ? (module.dimensions.depth / renderingSize)
        : (module.dimensions.width / renderingSize);
      const height = [90, 270].includes(computedRotation)
        ? (module.dimensions.width / renderingSize)
        : (module.dimensions.height / renderingSize);

      let left = module.position.x / renderingSize;
      let bottom = module.position.y / renderingSize;

      if (position.left !== undefined) {
        left = pixelParser(position.left);
      } else if (position.right !== undefined) {
        const right = pixelParser(position.right);
        if (cameraView === "left" || cameraView === "back") {
          let containerWidth = 0;
          if (currentRoom.walls && currentRoom.walls.length > 0) {
            containerWidth = Math.max(...currentRoom.walls.map(w => (w.position.x + w.dimensions.width) / renderingSize));
          } else if (currentRoom.dimensions?.width) {
            containerWidth = currentRoom.dimensions.width / renderingSize;
          }
          if (containerWidth > 0) {
            left = containerWidth - right - width;
          }
        } else {
          left = right - width;
        }
      }

      if (position.top !== undefined) {
        bottom = pixelParser(position.top);
      } else if (position.bottom !== undefined) {
        bottom = pixelParser(position.bottom);
      }

      const adjusted = labelPositions.get(module.id);

      // For left and back views, modules use 'right' positioning, so labels should too
      if (cameraView === "left" || cameraView === "back") {
        // Get the module's actual right position
        const moduleRight = position.right !== undefined ? pixelParser(position.right) : null;

        if (moduleRight !== null) {
          // Calculate module center relative to right edge
          // Module center from right = moduleRight + (width / 2)
          const moduleCenterFromRight = moduleRight + (width / 2);

          // Use adjusted position if available
          let labelCenterFromRight = moduleCenterFromRight;
          if (adjusted) {
            // adjusted.centerX is in left-based coordinates
            // Convert to right-based: centerFromRight = containerWidth - centerX
            let containerWidth = 0;
            if (currentRoom.walls && currentRoom.walls.length > 0) {
              containerWidth = Math.max(...currentRoom.walls.map(w => (w.position.x + w.dimensions.width) / renderingSize));
            } else if (currentRoom.dimensions?.width) {
              containerWidth = currentRoom.dimensions.width / renderingSize;
            }
            if (containerWidth > 0) {
              labelCenterFromRight = containerWidth - adjusted.centerX;
            }
          }

          // Position label: label center is at labelCenterFromRight from right
          // Label is 40px wide, so label's right edge = labelCenterFromRight - 20
          const labelRight = labelCenterFromRight - 20;

          const moduleCenterY = bottom + (height / 2);
          const centerY = adjusted ? adjusted.centerY : moduleCenterY;

          return {
            right: `${labelRight}px`,
            bottom: `${centerY - 20}px`,
          };
        }
      }

      // For other views, use left positioning
      const moduleCenterX = left + (width / 2);
      const moduleCenterY = bottom + (height / 2);

      const centerX = adjusted ? adjusted.centerX : moduleCenterX;
      const centerY = adjusted ? adjusted.centerY : moduleCenterY;

      return {
        left: `${centerX - 20}px`,
        bottom: `${centerY - 20}px`,
      };
    };

    return (
      <div
        className="position-absolute w-100 h-100"
        style={{
          top: 0,
          left: 0,
          pointerEvents: "none",
          overflow: "visible",
          zIndex: zIndexMax + 20000,
          transform: "translateZ(0)",
          isolation: "isolate",
        }}
      >
        {allModules.map(({ module, group }) => {
          const pos = getModulePos(module, group);
          return (
            <div
              key={module.id}
              className="position-absolute d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
              style={{
                ...pos,
                width: "40px",
                height: "40px",
                fontSize: "26px",
                backgroundColor: "#000",
                zIndex: zIndexMax + 20000,
                pointerEvents: "none",
                transform: "translateZ(0)",
                isolation: "isolate",
              }}
            >
              {module.label}
            </div>
          );
        })}
      </div>
    );
  };

  const resolveLabelOverlaps = useCallback(() => {
    if (showModuleLabels !== "all") {
      setLabelPositions(new Map());
      return;
    }

    const allowedRotations = getRotationsForWallView(cameraView, wallView);
    const allModules = [
      ...currentRoom.groups
        .filter((group) => allowedRotations.includes(group.rotation))
        .flatMap(group =>
          group.modules
            ?.filter(() => allowedRotations.includes(group.rotation))
            .map(m => ({ module: m, group })) || []
        ),
      ...(currentRoom.modules
        ?.filter((module) => allowedRotations.includes(module.rotation))
        .map(m => ({ module: m, group: null })) || [])
    ];

    const getModuleCenter = (module, group) => {
      let rotation = module.rotation;
      if (rotation === 0 && group) rotation = group.rotation;
      const computedRotation = computeRotation(rotation, cameraView);

      if (isTopView()) {
        let x = module.position.x / renderingSize;
        let z = (module.position.z || 0) / renderingSize;

        if (group) {
          if (rotation === 90) {
            const groupFrontZ = group.position.z + group.dimensions.depth;
            const moduleFrontZ = module.position.z + module.dimensions.depth;
            x = (group.position.x + (groupFrontZ - moduleFrontZ)) / renderingSize;
            z = (group.position.z - (group.position.x - module.position.x)) / renderingSize;
          } else if (rotation === 180) {
            const groupX2 = group.position.x + group.dimensions.width;
            const moduleX2 = module.position.x + module.dimensions.width;
            x = (group.position.x + (groupX2 - moduleX2)) / renderingSize;
            const groupFrontZ = group.position.z + group.dimensions.depth;
            const moduleFrontZ = module.position.z + module.dimensions.depth;
            z = (group.position.z + (groupFrontZ - moduleFrontZ)) / renderingSize;
          } else if (rotation === 270) {
            x = (group.position.x - (group.position.z - module.position.z)) / renderingSize;
            const groupX2 = group.position.x + group.dimensions.width;
            const moduleX2 = module.position.x + module.dimensions.width;
            z = (group.position.z + (groupX2 - moduleX2)) / renderingSize;
          }
        }

        return {
          x: x + (module.dimensions.width / renderingSize / 2),
          y: z + (module.dimensions.depth / renderingSize / 2),
        };
      }

      const spec = getModulePositionSpec(cameraView, computedRotation, !!group);
      const position = computeModulePosition(group, module, spec, renderingSize);
      const pixelParser = (str) => parseFloat(str.replace("px", "")) || 0;

      const width = [90, 270].includes(computedRotation)
        ? (module.dimensions.depth / renderingSize)
        : (module.dimensions.width / renderingSize);
      const height = [90, 270].includes(computedRotation)
        ? (module.dimensions.width / renderingSize)
        : (module.dimensions.height / renderingSize);

      let left = module.position.x / renderingSize;
      let bottom = module.position.y / renderingSize;

      if (position.left !== undefined) {
        left = pixelParser(position.left);
      } else if (position.right !== undefined) {
        const right = pixelParser(position.right);
        if (cameraView === "left" || cameraView === "back") {
          let containerWidth = 0;
          if (currentRoom.walls && currentRoom.walls.length > 0) {
            containerWidth = Math.max(...currentRoom.walls.map(w => (w.position.x + w.dimensions.width) / renderingSize));
          } else if (currentRoom.dimensions?.width) {
            containerWidth = currentRoom.dimensions.width / renderingSize;
          }
          if (containerWidth > 0) {
            left = containerWidth - right - width;
          }
        } else {
          left = right - width;
        }
      }

      if (position.top !== undefined) {
        bottom = pixelParser(position.top);
      } else if (position.bottom !== undefined) {
        bottom = pixelParser(position.bottom);
      }

      return {
        x: left + (width / 2),
        y: bottom + (height / 2),
      };
    };

    const positions = new Map();
    const labelSize = 50;
    const labelData = allModules.map(({ module, group }) => {
      const center = getModuleCenter(module, group);
      return {
        id: module.id,
        x: center.x,
        y: center.y,
      };
    });

    labelData.sort((a, b) => a.y - b.y || a.x - b.x);

    labelData.forEach((current, index) => {
      if (index === 0) {
        positions.set(current.id, { centerX: current.x, centerY: current.y });
        return;
      }

      let { x, y } = current;
      for (let i = 0; i < index; i++) {
        const prev = labelData[i];
        if (Math.abs(x - prev.x) < labelSize && Math.abs(y - prev.y) < labelSize) {
          x = prev.x + labelSize;
          if (Math.abs(y - prev.y) < labelSize) y = prev.y + labelSize;
        }
      }

      positions.set(current.id, { centerX: x, centerY: y });
    });

    setLabelPositions(positions);
  }, [currentRoom, showModuleLabels, renderingSize, cameraView, wallView, isTopView]);

  // Disabling overlap resolution logic since it creates issues.
  // useEffect(() => {
  //   resolveLabelOverlaps();
  // }, [resolveLabelOverlaps]);

  function RenderNode(props) {
    return (
      <RenderCanvasElement
        renderingsize={renderingSize}
        data-object-key={props.object ? getObjectKey(props.object) : undefined}
        data-object-type={props.object?.type}
        // FIXME: Using zoom to calculate the outline slows the canvas.
        // zoom={zoom}
        className={`node`}
        {...props}
      >
        {props.children}
      </RenderCanvasElement>
    );
  }

  const renderRoom = () => {
    const allowedRotations = getRotationsForWallView(cameraView, wallView);

    return (
      <div>
        {currentRoom.walls.map((wall, wallIndex) => (
          <WallNode
            key={wallIndex}
            wall={wall}
            isAnyConditionOn={isAnyConditionOn}
          ></WallNode>
        ))}

        {currentRoom.groups
          .filter((group) => allowedRotations.includes(group.rotation))
          .map((group, groupIndex) => (
            <>
              <GroupNode
                key={groupIndex}
                group={group}
                isAnyConditionOn={isAnyConditionOn}
              ></GroupNode>

              {group.modules
                .filter(() => allowedRotations.includes(group.rotation))
                .map((module, moduleIndex) => (
                  <>
                    <ModuleNode
                      key={moduleIndex}
                      module={module}
                      group={group}
                      isAnyConditionOn={isAnyConditionOn}
                    ></ModuleNode>
                    {shouldShowDepthMeasurement(module) &&
                      renderDepthMeasurements(module, group)}
                  </>
                ))}

              {group.walls?.map((wall, wallIndex) => (
                <WallNode
                  key={wallIndex}
                  wall={wall}
                  isAnyConditionOn={isAnyConditionOn}
                ></WallNode>
              ))}
            </>
          ))}

        {currentRoom.modules
          .filter((module) => allowedRotations.includes(module.rotation))
          .map((module, moduleIndex) => (
            <>
              <ModuleNode
                key={moduleIndex}
                module={module}
                isAnyConditionOn={isAnyConditionOn}
              />
              {shouldShowDepthMeasurement(module) &&
                renderDepthMeasurements(module, null)}
            </>
          ))}
      </div>
    );
  };

  function renderDragSelection() {
    if (rectSelector.active) {
      const rect = containerRef.current.getBoundingClientRect();
      const left =
        Math.min(rectSelector.startPos.x, rectSelector.currentPos.x) - rect.x;
      const top =
        Math.min(rectSelector.startPos.y, rectSelector.currentPos.y) - rect.y;

      return (
        <div
          className="rect-selector"
          style={{
            left: left,
            top: top,
            width: Math.abs(
              rectSelector.currentPos.x - rectSelector.startPos.x
            ),
            height: Math.abs(
              rectSelector.startPos.y - rectSelector.currentPos.y
            ),
            outline: "3px solid blue",
          }}
        ></div>
      );
    }
  }

  function GroupNode({ group, isAnyConditionOn }) {
    const style = { pointerEvents: "none" };

    let rotation = group.rotation;

    const computedRotation = computeRotation(rotation, cameraView);

    if ([90, 270].includes(computedRotation))
      style.width = `${group.dimensions.depth / renderingSize}px`;

    if (isTopView()) {
      style.top = `${group.position.z / renderingSize}px`;

      style.height = [90, 270].includes(rotation)
        ? `${group.dimensions.width / renderingSize}px`
        : `${group.dimensions.depth / renderingSize}px`;
    } else if (cameraView === "right") {
      style.left = `${group.position.z / renderingSize}px`;
    } else if (cameraView === "left") {
      style.left = "auto";
      style.right = `${group.position.z / renderingSize}px`;
    } else if (cameraView === "back") {
      style.left = "auto";
      style.right = `${group.position.x / renderingSize}px`;
    }

    return (
      <RenderNode
        object={group}
        className={`group-node bg-light-grey ${isSelected(group) && "active"}
          ${isAnyConditionOn ? "blurred-light" : ""}`}
        style={style}
      ></RenderNode>
    );
  }

  function WallNode({ wall, isAnyConditionOn }) {
    return (
      <RenderNode
        object={wall}
        className={`wall-node ${isSelected(wall) && "active"}
         ${isAnyConditionOn ? "blurred-light" : ""}`}
        onMouseDown={(event) => {
          clickHandler(event, wall);
        }}
        onMouseEnter={() => {
          setHoverItem(wall);
        }}
        onMouseLeave={() => {
          setHoverItem(null);
        }}
      ></RenderNode>
    );
  }

  function onModuleHover(module) {
    let selections = selectionManagerRef.current.selections();
    let selectedObjectSiblings = false;

    if (
      selections.some((selection) => selection.group_id === module.group_id)
    ) {
      selectedObjectSiblings = true;
    }

    if (!selectedObjectSiblings && module.group_id) {
      const group = selectionManager.findGroup(module.group_id);

      if (group) {
        setHoverItem(group);
      }
    } else if (selectedObjectSiblings && module.group_id) {
      const parentGroup = selectionManager.findGroup(module.group_id);

      if (parentGroup) {
        const siblingModules = parentGroup.modules.find(
          (m) => m.id === module.id
        );

        if (siblingModules) setHoverItem(siblingModules);
      }
    } else {
      setHoverItem(module);
    }
  }

  function onSelectionHover(selection, module) {
    if (selection.type == "shutter") {
      if (
        module.template.shutters.some(
          (selection) => selection.order_item_id === module.id
        )
      ) {
        setHoverItem(selection);
      }
    } else if (selection.type == "partition") {
      if (
        module.template.partitions.some(
          (selection) => selection.order_item_id === module.id
        )
      ) {
        setHoverItem(selection);
      }
    } else if (selection.type === "space") {
      if (selectionManager.selection().is_root_space) {
        setHoverItem(selection);
        return;
      }
    }
  }

  function ModuleNode({ module, group, isAnyConditionOn }) {
    const style = {};
    const isStale = projectSummary.stale_order_item_ids?.includes(module.id);
    let rotation = module.rotation;

    if (rotation === 0 && group) rotation = group.rotation;

    const computedRotation = computeRotation(rotation, cameraView);

    if ([90, 270].includes(computedRotation))
      style.width = `${module.dimensions.depth / renderingSize}px`;

    if (isTopView()) {
      // Calculate left position when module is in a group
      if (group) {
        if (rotation === 90) {
          const groupFrontZ = group.position.z + group.dimensions.depth;
          const moduleFrontZ = module.position.z + module.dimensions.depth;

          style.left = `${
            (group.position.x + (groupFrontZ - moduleFrontZ)) / renderingSize
          }px`;
        } else if (rotation === 180) {
          const groupX2 = group.position.x + group.dimensions.width;
          const moduleX2 = module.position.x + module.dimensions.width;

          style.left = `${
            (group.position.x + (groupX2 - moduleX2)) / renderingSize
          }px`;
        } else if (rotation === 270) {
          style.left = `${
            (group.position.x - (group.position.z - module.position.z)) /
            renderingSize
          }px`;
        }
      }

      // Calculate top position
      if (rotation === 90 && group) {
        style.top = `${
          (group.position.z - (group.position.x - module.position.x)) /
          renderingSize
        }px`;
      } else if (rotation === 180 && group) {
        const groupFrontZ = group.position.z + group.dimensions.depth;
        const moduleFrontZ = module.position.z + module.dimensions.depth;

        style.top = `${
          (group.position.z + (groupFrontZ - moduleFrontZ)) / renderingSize
        }px`;
      } else if (rotation === 270 && group) {
        const groupX2 = group.position.x + group.dimensions.width;
        const moduleX2 = module.position.x + module.dimensions.width;

        style.top = `${
          (group.position.z + (groupX2 - moduleX2)) / renderingSize
        }px`;
      } else {
        style.top = `${module.position.z / renderingSize}px`;
      }

      // Set height and width based on side view
      if ([90, 270].includes(rotation)) {
        style.width = `${module.dimensions.depth / renderingSize}px`;
        style.height = `${module.dimensions.width / renderingSize}px`;
      } else {
        style.height = `${module.dimensions.depth / renderingSize}px`;
      }
    } else {
      const spec = getModulePositionSpec(cameraView, computedRotation, !!group);

      const position = computeModulePosition(
        group,
        module,
        spec,
        renderingSize
      );

      Object.assign(style, position);

      const zIndex = computeModuleZIndex(group, module, spec, zIndexMax);
      style.zIndex = Math.round(zIndex);
    }

    return (
      <RenderNode
        style={style}
        object={module}
        className={`module-node ${isSelected(module) && "active"}
         ${
           isAnyConditionOn && shouldBlurModule(module) ? "blurred-light" : ""
         }`}
        onMouseEnter={() => onModuleHover(module)}
        onMouseLeave={() => setHoverItem(null)}
      >
        {module.template && !isStale && (
          <>
            <ComponentNode
              module={module}
              className="base-component"
              component={module.template.base_component}
              onMouseDown={(event) =>
                clickHandler(event, module.template.base_component)
              }
              rotation={computedRotation}
            >
              {showModuleLabels &&
                showModuleLabels == "shutters" &&
                !showShutters &&
                module.template.base_component?.label && (
                  <div
                    className="fw-bold"
                    style={{
                      position: "absolute",
                      bottom: `${
                        module.template.base_component.dimensions.height /
                        renderingSize
                      }px`,
                      left: `${
                        module.template.base_component.dimensions.width /
                        renderingSize /
                        2
                      }px`,
                      alignContent: "center",
                      textAlign: "center",
                      opacity: 1,
                      fontSize: "8px",
                      height: "20px",
                      width: "20px",
                      borderRadius: "50%",
                      backgroundColor: "black",
                      color: "white",
                      zIndex: "9999",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {module.template.base_component.label}
                  </div>
                )}
            </ComponentNode>
            <SpaceNode
              className="root-space"
              space={module.template.root_space}
              parentSpace={module}
              module={module}
              group={group}
              rotation={computedRotation}
            ></SpaceNode>
            <div className="partitions">
              {module.template.partitions.map((partition, partitionIndex) => (
                <PartitionNode
                  key={partitionIndex}
                  partition={partition}
                  module={module}
                  group={group}
                  rotation={computedRotation}
                ></PartitionNode>
              ))}
            </div>
            {showShutters && (
              <div className="shutters">
                {module.template.shutters.map((shutter, shutterIndex) => (
                  <ShutterNode
                    className="shutter-node"
                    key={shutterIndex}
                    shutter={shutter}
                    module={module}
                    group={group}
                    rotation={computedRotation}
                  ></ShutterNode>
                ))}
              </div>
            )}
          </>
        )}

        {isStale && (
          <>
            <div
              className="position-absolute h-100 w-100 opacity-25 bg-black"
              style={{ zIndex: 10 }}
              id={`stale-module-${module.id}`}
              onMouseDown={(event) => {
                if (module.template)
                  clickHandler(event, module.template.base_component);
              }}
            >
              <div className="position-absolute top-50 start-50 translate-middle text-center">
                <i
                  className="fa-solid fa-circle-exclamation text-white fs-1"
                  style={{ fontSize: "3rem" }}
                />
              </div>
            </div>
            <UncontrolledTooltip
              target={`stale-module-${module.id}`}
              placement="bottom"
            >
              Preview is outdated. Refresh using the header.
            </UncontrolledTooltip>
          </>
        )}
      </RenderNode>
    );
  }

  function PartitionNode({ partition, module, rotation }) {
    return (
      <ComponentNode
        component={partition.component}
        module={module}
        onMouseDown={(event) => clickHandler(event, partition)}
        onMouseEnter={() => {
          if (
            selectionManager.selection() &&
            selectionManager.selection().type == "partition"
          ) {
            onSelectionHover(partition, module);
          }
        }}
        onMouseLeave={() => {
          setHoverItem(null);
        }}
        className={`partition-node ${isSelected(partition) && "active"}`}
        rotation={rotation}
      >
        {showModuleLabels &&
          showModuleLabels == "shutters" &&
          !showShutters &&
          partition.component?.label && (
            <div
              className="fw-bold"
              style={{
                position: "absolute",
                bottom: `${
                  partition.component.type === "v_partition"
                    ? partition.component.dimensions.height / renderingSize / 2
                    : partition.component.dimensions.height / renderingSize
                }px`,
                left: `${
                  partition.component.type === "v_partition"
                    ? "0"
                    : partition.component.dimensions.width / renderingSize / 3
                }px`,
                alignContent: "center",
                textAlign: "center",
                opacity: 1,
                fontSize: "8px",
                height: "20px",
                width: "20px",
                borderRadius: "50%",
                backgroundColor: "black",
                color: "white",
                zIndex: "9999",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {partition.component.label}
            </div>
          )}
      </ComponentNode>
    );
  }

  function ShutterNode({ shutter, module, rotation }) {
    if (
      shutter.is_removed &&
      !showRemovedShutters &&
      !module.has_profile_shutter
    )
      return;
    return (
      <ComponentNode
        component={shutter.component}
        module={module}
        hasProfileShutter={module.has_profile_shutter}
        handle={module.handle}
        onMouseDown={(event) => clickHandler(event, shutter)}
        onMouseEnter={() => {
          if (
            selectionManager.selection() &&
            selectionManager.selection().type == "shutter"
          ) {
            onSelectionHover(shutter, module);
          }
        }}
        onMouseLeave={() => {
          setHoverItem(null);
        }}
        className={`${isSelected(shutter) && "active"}`}
        rotation={rotation}
      >
        {showModuleLabels === "shutters" && shutter.component?.label && (
          <div
            className="fw-bold"
            style={{
              position: "absolute",
              bottom: `${
                shutter.component.dimensions.height / renderingSize / 2
              }px`,
              left: `${
                shutter.component.dimensions.width / renderingSize / 3
              }px`,
              alignContent: "center",
              textAlign: "center",
              opacity: 1,
              fontSize: "16px",
              height: "40px",
              width: "40px",
              borderRadius: "50%",
              backgroundColor: "black",
              color: "white",
              zIndex: "9999",
            }}
          >
            {shutter.component.label}
          </div>
        )}
      </ComponentNode>
    );
  }

  function SpaceNode({
    space,
    parentSpace,
    widthCount = -1,
    heightCount = -1,
    module,
    rotation,
  }) {
    if (!space) return;
    if (space.width) widthCount++;
    if (space.height) heightCount++;

    // Apply transformation using the generic system
    const transform = applyTransform(
      space,
      parentSpace,
      { rotYDeg: rotation, cameraView },
      renderingSize
    );

    const style = { ...transform.style, zIndex: transform.zIndex };

    return (
      <RenderNode
        style={style}
        object={space}
        onMouseDown={(event) => clickHandler(event, space)}
        onMouseEnter={() => {
          if (
            selectionManager.selection() &&
            selectionManager.selection().type == "space"
          ) {
            onSelectionHover(space, module);
          }
        }}
        onMouseLeave={() => {
          setHoverItem(null);
        }}
        className={`space-node ${
          isSelected(space) && "active"
        } ${isRemovableSpace("replaceable-space")}`}
        key={space}
      >
        {space.children &&
          space.children.map((childSpace, spaceIndex) => (
            <SpaceNode
              key={spaceIndex}
              space={childSpace}
              parentSpace={space}
              widthCount={widthCount}
              heightCount={heightCount}
              module={module}
              rotation={rotation}
            ></SpaceNode>
          ))}
        {space.is_drawer && (
          <div className="h-100 pattern border border-black">
            {showLabels && renderLabels(space)}
          </div>
        )}
        {showModuleLabels === "shutters" &&
          !showShutters &&
          space.component?.label && (
            <div
              className="fw-bold"
              style={{
                position: "absolute",
                bottom: `${
                  space.is_drawer
                    ? space.dimensions.height / renderingSize / 5
                    : space.dimensions.height / renderingSize
                }px`,
                left: `${space.dimensions.width / renderingSize / 3}px`,
                alignContent: "center",
                textAlign: "center",
                opacity: 1,
                fontSize: "8px",
                height: "20px",
                width: "20px",
                borderRadius: "50%",
                backgroundColor: "black",
                color: "white",
                zIndex: "9999",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {space.component.label}
            </div>
          )}
        {showModuleLabels === "spaces" &&
          !showShutters &&
          space.is_leaf_space &&
          space.path && (
            <div
              className="fw-bold"
              style={{
                position: "absolute",
                bottom: `${
                  space.is_drawer
                    ? space.dimensions.height / renderingSize / 5
                    : space.dimensions.height / renderingSize / 2
                }px`,
                left: `${space.dimensions.width / renderingSize / 2}px`,
                transform: "translateX(-50%)",
                alignContent: "center",
                textAlign: "center",
                opacity: 1,
                fontSize: "8px",
                minHeight: "20px",
                minWidth: "20px",
                padding: "0 4px",
                borderRadius: "10px",
                backgroundColor: isSelected(space) ? "#2C92E6" : "#000",
                color: "white",
                zIndex: "9999",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {formatLeafSpacePath(space.path)}
            </div>
          )}
        {showMeasurements && cameraView !== "top" && !space.is_root_space && (
          <>
            {space.width && (
              <div
                className="d-flex position-absolute"
                style={{
                  top: `${widthCount * 20}px`,
                  left: "12px",
                  width: space.dimensions["width"] / renderingSize - 15,
                  color: "blue",
                }}
              >
                <InternalsDimensionIcon className="w-100" dimension={"width"} />
                <div
                  style={{ fontSize: "10px" }}
                  className="w-100 d-flex justify-content-center"
                >
                  <span>{space.width}</span>
                </div>
              </div>
            )}
            {space.height && (
              <div
                className="d-flex position-absolute"
                style={{
                  top: "6px",
                  left: `${heightCount * 20}px`,
                  height: space.dimensions["height"] / renderingSize - 9,
                  color: "blue",
                }}
              >
                <InternalsDimensionIcon
                  className="h-100"
                  dimension={"height"}
                />
                {/* Use SVG for vertical text (internal height measurements) - html2canvas supports SVG correctly */}
                <svg
                  width="20"
                  height={space.dimensions["height"] / renderingSize - 9}
                  viewBox={`0 0 20 ${
                    space.dimensions["height"] / renderingSize - 9
                  }`}
                  preserveAspectRatio="none"
                  style={{
                    flexGrow: 1,
                    height: "100%",
                    display: "block",
                  }}
                >
                  <text
                    x="10"
                    y={(space.dimensions["height"] / renderingSize - 9) / 2}
                    writingMode="vertical-rl"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="10"
                    fill="currentColor"
                    textOrientation="upright"
                    transform={`rotate(180, 10, ${
                      (space.dimensions["height"] / renderingSize - 9) / 2
                    })`}
                  >
                    {String(space.height)}
                  </text>
                </svg>
              </div>
            )}
          </>
        )}
      </RenderNode>
    );
  }

  function ComponentNode(props) {
    const component = props.component;
    const componentIndex = 0;
    const hasProfileShutter = props.hasProfileShutter;

    const module = props.module;
    const rotation = props.rotation;

    // Apply transformation using the generic system
    const transform = applyTransform(
      component,
      module,
      { rotYDeg: rotation, cameraView },
      renderingSize
    );

    const style = { ...transform.style };

    if (component.type !== "carcass") style.zIndex = transform.zIndex;

    return (
      <RenderNode
        style={style}
        object={component}
        className={`component-node`}
        key={componentIndex}
        {...props}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {props.handle?.show &&
            (() => {
              const side =
                props.handle.config.shutter_directions[
                  component.sequence_number - 1
                ];
              return (
                <div
                  className="position-absolute d-flex"
                  style={{
                    [side]: 0,
                    height: ["top", "bottom"].includes(side)
                      ? `${props.handle.config.offset / renderingSize}px`
                      : "100%",
                    width: ["top", "bottom"].includes(side)
                      ? "100%"
                      : `${props.handle.config.offset / renderingSize}px`,
                  }}
                >
                  <img
                    src={handlesImage[props.handle.type][side]}
                    className="w-100 h-100"
                  />
                </div>
              );
            })()}
          {component.panels.map((panel, panelIndex) => (
            <PanelNode
              key={panelIndex}
              panel={panel}
              component={component}
              hasProfileShutter={hasProfileShutter}
              rotation={rotation}
            ></PanelNode>
          ))}
          {showLabels && renderLabels(component)}
          {props.children}
        </div>
      </RenderNode>
    );
  }

  function PanelNode({ panel, component, hasProfileShutter, rotation }) {
    const componentType = component.type;
    let backgroundColor =
      componentType === "shutter" && !hasProfileShutter ? "#d9d9d9" : "none";

    let border = "1px solid black";
    let backgroundImage =
      componentType === "shutter" && hasProfileShutter
        ? `url(${profileGlassShutter})`
        : "none";

    let backgroundRepeat =
      componentType === "shutter" && hasProfileShutter ? "no-repeat" : "none";

    let backgroundSize =
      componentType === "shutter" && hasProfileShutter ? "100% 100%" : "none";

    let boxShadow = "none";

    if (panel.front_laminate && showColors && !hasProfileShutter) {
      if (panel.front_laminate.image_url) {
        backgroundImage = `url("${panel.front_laminate.image_url}")`;
        backgroundRepeat = "repeat";
        backgroundSize = `${
          panel.front_laminate.image_width / renderingSize
        }px ${panel.front_laminate.image_height / renderingSize}px`;
      } else if (panel.front_laminate.color_code) {
        backgroundColor = panel.front_laminate.color_code;
      }
    } else {
      // No laminate applied.
      backgroundColor = "#d9d9d9";
    }

    if (panel.orientation != "surface") {
      // Edge
      if (component.identifier === "removable_shelf") {
        border = "1px dashed black";
      } else {
        // Fixed partition - fill with black.
        backgroundColor = "black";
      }
    }

    const transform = applyPanelTransform(
      panel,
      component,
      { rotYDeg: rotation, cameraView },
      renderingSize
    );

    const visibleLaminate = getVisibleLaminate(panel, transform.spec);

    if (visibleLaminate && showColors && !hasProfileShutter) {
      if (visibleLaminate.image_url) {
        backgroundImage = `url("${visibleLaminate.image_url}")`;
        backgroundRepeat = "repeat";
        backgroundSize = visibleLaminate.image_width
          ? `${visibleLaminate.image_width / renderingSize}px ${
              visibleLaminate.image_height / renderingSize
            }px`
          : "100% 100%";
      } else if (visibleLaminate.color_code) {
        backgroundColor = visibleLaminate.color_code;
      }
    }

    const style = {
      ...transform.style,
      zIndex: transform.zIndex,
      border: border,
      backgroundColor: backgroundColor,
      backgroundImage: backgroundImage,
      backgroundRepeat: backgroundRepeat,
      backgroundSize: backgroundSize,
      boxShadow: boxShadow,
    };

    return (
      <RenderNode
        object={panel}
        className="panel-node"
        style={style}
        key={panel.uniq_id}
      >
        {panel.orientation === "surface" &&
          ((panel.cnc_type && panel.cnc_type != "none") ||
            (panel.pattern_type && panel.pattern_type != "none")) && (
            <div className="position-absolute top-0 end-0 d-flex flex-column align-items-end gap-1">
              {panel.cnc_type && panel.cnc_type != "none" && (
                <div className="bg-dark bg-opacity-50 text-white px-2 fw-bold">
                  CNC
                </div>
              )}
              {panel.pattern_type && panel.pattern_type != "none" && (
                <div className="bg-dark bg-opacity-50 text-white px-1 small fw-bold">
                  PATTERN
                </div>
              )}
            </div>
          )}
      </RenderNode>
    );
  }

  // ######################################################################### EVENT HANDLING #########################################################################

  let timer = useRef();

  const clickHandler = (event, object) => {
    handleItemClick(event, object);
  };

  const centerCanvasOnPosition = (x, y, zoomValue, view = null) => {
    const rect = containerRef.current.getBoundingClientRect();

    // Needed because the height of the div extends off screen
    const visibleHeight = Math.min(rect.height, window.innerHeight - rect.top);

    // For left and back views, items use right positioning, so we need to account for container width
    let adjustedX = x;
    if (view === "left" || view === "back") {
      let containerWidth = 0;
      if (currentRoom.walls && currentRoom.walls.length > 0) {
        if (view === "left") {
          // For left view, container width is based on z-axis (depth)
          containerWidth = Math.max(...currentRoom.walls.map(w => {
            const wallZ = w.position.z || 0;
            const wallDepth = w.dimensions.depth || 0;
            return wallZ + wallDepth;
          }));
        } else {
          // For back view, container width is based on x-axis (width)
          containerWidth = Math.max(...currentRoom.walls.map(w => w.position.x + w.dimensions.width));
        }
      } else if (currentRoom.dimensions) {
        if (view === "left" && currentRoom.dimensions.depth) {
          containerWidth = currentRoom.dimensions.depth;
        } else if (view === "back" && currentRoom.dimensions.width) {
          containerWidth = currentRoom.dimensions.width;
        }
      }

      if (containerWidth > 0) {
        adjustedX = x;
      }
    }

    const newPosition = {
      x: -1 * ((adjustedX * zoomValue) / renderingSize - rect.width / 2),
      y: -1 * ((y * zoomValue) / renderingSize - visibleHeight / 2),
    };

    setIsCentering(true);
    if (newPosition.x && newPosition.y) setPosition(newPosition);
  };

  // Calculate screen position based on camera view
  const getScreenPositionForCentering = (item, group = null) => {
    let screenX, screenY;

    if (cameraView === "top") {
      // Top view: x and y map directly
      screenX = item.position.x + (item.dimensions?.width || 0) / 2;
      screenY = item.position.y + (item.dimensions?.height || 0) / 2;
    } else if (cameraView === "front") {
      // Front view: x and y map directly
      screenX = item.position.x + (item.dimensions?.width || 0) / 2;
      screenY = item.position.y + (item.dimensions?.height || 0) / 2;
    } else if (cameraView === "right") {
      // Right view: z maps to x (left positioning), y stays the same
      // Items are positioned with: left = z / renderingSize
      screenX = item.position.z + (item.dimensions?.depth || 0) / 2;
      screenY = item.position.y + (item.dimensions?.height || 0) / 2;
    } else if (cameraView === "left") {
      let containerWidth = 0;
      if (currentRoom.walls && currentRoom.walls.length > 0) {
        containerWidth = Math.max(...currentRoom.walls.map(w => {
          const wallZ = w.position.z || 0;
          const wallDepth = w.dimensions.depth || 0;
          return wallZ + wallDepth;
        }));
      } else if (currentRoom.dimensions?.depth) {
        containerWidth = currentRoom.dimensions.depth;
      }

      if (containerWidth > 0) {
        const zCenter = item.position.z + (item.dimensions?.depth || 0) / 2;
        // Convert from right-positioned to left-positioned coordinate
        screenX = containerWidth - zCenter;
      } else {
        // Fallback: use z directly (may not center correctly but won't crash)
        screenX = item.position.z + (item.dimensions?.depth || 0) / 2;
      }
      screenY = item.position.y + (item.dimensions?.height || 0) / 2;
    } else if (cameraView === "back") {
        let containerWidth = 0;
      if (currentRoom.walls && currentRoom.walls.length > 0) {
        containerWidth = Math.max(...currentRoom.walls.map(w => w.position.x + w.dimensions.width));
      } else if (currentRoom.dimensions?.width) {
        containerWidth = currentRoom.dimensions.width;
      }

      const xCenter = item.position.x + (item.dimensions?.width || 0) / 2;

      if (containerWidth > 0) {
        // Item's center in left-based coordinate system
        screenX = containerWidth - xCenter;
      } else {
        // If no container width, try using x directly (might not work perfectly)
        screenX = xCenter;
      }
      screenY = item.position.y + (item.dimensions?.height || 0) / 2;
    } else {
      // Default to front view
      screenX = item.position.x + (item.dimensions?.width || 0) / 2;
      screenY = item.position.y + (item.dimensions?.height || 0) / 2;
    }

    return { screenX, screenY };
  };

  const visibleRange = (zoomAmount) => {
    const rect = containerRef.current.getBoundingClientRect();
    // Needed because the height of the div extends off screen
    const visibleHeight = Math.min(rect.height, window.innerHeight - rect.top);

    const range = {
      xMin: (-1 * position.x * renderingSize) / zoomAmount,
      yMin:
        ((rect.height - visibleHeight) * renderingSize) / zoomAmount +
        (-1 * position.y * renderingSize) / zoomAmount,
      xMax: ((-1 * position.x + rect.width) * renderingSize) / zoomAmount,
      yMax: ((-1 * position.y + visibleHeight) * renderingSize) / zoomAmount,
    };
    return range;
  };

  const isItemVisible = (itemBounds, rect) => {
    const isXPartiallyVisible =
      itemBounds.xMax > rect.xMax && itemBounds.xMin < rect.xMax;
    const isYPartiallyVisible =
      itemBounds.yMax > rect.yMin && itemBounds.yMin < rect.yMax;

    return isXPartiallyVisible && isYPartiallyVisible;
  };

  const getBounds = (position, dimensions) => {
    return {
      xMin: position.x,
      xMax: position.x + dimensions.width / renderingSize,
      yMin: position.y,
      yMax: position.y + dimensions.height / renderingSize,
    };
  };

  const resetItem = () => {
    if (isReset) {
      // Only center if exactly ONE item or ONE group is selected
      const selections = selectionManager.selections();
      if (selections.length !== 1) {
        setIsReset(false);
        return;
      }

      let items = selectionManager.selection();

      if (items) {
        // let zoomValue = zoom;
        // if (zoom > 1.5 || zoom < 0.3) {
        //   setZoom(0.5);
        //   zoomValue = 0.5;
        // }
        let itemPosition;
        let itemDimensions;

        const visibleRangeResult = visibleRange(zoom);

        if (items.group_id) {
          let group = currentRoom.groups.find(
            (grp) => grp.id === items.group_id
          );

          if (group) {
            itemPosition = group.position;
            itemDimensions = group.dimensions;

            // Always center when exactly one group is selected
            centerCanvasOnPosition(
              group.position.x + group.dimensions.width / 2,
              group.position.y + group.dimensions.height / 2,
              zoom
            );
          }
        } else {
          itemPosition = items.position;
          itemDimensions = items.dimensions;

          // Always center when exactly one item is selected
          centerCanvasOnPosition(
            items.position.x + items.dimensions.width / 2,
            items.position.y + items.dimensions.height / 2,
            zoom
          );
        }
        setIsReset(false);
      }
    }
  };

  useEffect(() => {
    resetItem();
  }, [isReset]);

  // Auto-center when exactly one item or one group is selected
  const lastCenteredRef = useRef(null);
  const isCenteringRef = useRef(false);

  useEffect(() => {
    selectionManagerRef.current = selectionManager;
  }, [selectionManager]);

  useEffect(() => {
    if (!shouldCenterOnSelection || isCenteringRef.current) {
      return;
    }

    if (!containerRef.current || !currentRoom) {
      if (typeof setShouldCenterOnSelection === "function") {
        setShouldCenterOnSelection(false);
      }
      return;
    }

    const sm = selectionManagerRef.current;
    const selections = sm.selections();

    if (selections.length !== 1) {
      if (typeof setShouldCenterOnSelection === "function") {
        setShouldCenterOnSelection(false);
      }
      return;
    }

    const items = sm.selection();
    if (!items) {
      if (typeof setShouldCenterOnSelection === "function") {
        setShouldCenterOnSelection(false);
      }
      return;
    }

    const currentKey = sm.getKey
      ? sm.getKey()
      : JSON.stringify(selections);

    if (currentKey === lastCenteredRef.current) {
      if (typeof setShouldCenterOnSelection === "function") {
        setShouldCenterOnSelection(false);
      }
      return;
    }

    isCenteringRef.current = true;

    if (typeof setShouldCenterOnSelection === "function") {
      setShouldCenterOnSelection(false);
    }

    lastCenteredRef.current = currentKey;

    let centerX, centerY;

    let itemToCenter = items;
    let group = null;

    if (items.group_id) {
      group = currentRoom.groups?.find(
        (grp) => grp.id === items.group_id
      );
      if (group) {
        itemToCenter = group;
      }
    }

    const { screenX, screenY } = getScreenPositionForCentering(itemToCenter, group);
    centerX = screenX;
    centerY = screenY;

    if (centerX !== undefined && centerY !== undefined && !isNaN(centerX) && !isNaN(centerY)) {
      requestAnimationFrame(() => {
        if (containerRef.current && isCenteringRef.current) {
          centerCanvasOnPosition(centerX, centerY, zoom, cameraView);
          setTimeout(() => {
            isCenteringRef.current = false;
          }, 300);
        } else {
          isCenteringRef.current = false;
        }
      });
    } else {
      isCenteringRef.current = false;
    }
  }, [shouldCenterOnSelection, currentRoom, zoom, cameraView, setShouldCenterOnSelection]);

  useEffect(() => {
    if (showMeasurements == "hw") {
      generateMeasurements();
    }
  }, [showMeasurements, cameraView]);

  // ########################################################################################

  // Auto Capture Configuration for RoomCanvas
  const originalStateRef = useRef(null);

  const configureCanvasForImageType = useCallback(
    (imageType, viewConfig = null) => {
      if (!originalStateRef.current) {
        originalStateRef.current = {
          showShutters,
          showMeasurements,
          showLabels,
          showModuleLabels,
          show3d,
          cameraView,
          wallView,
        };
      }

      // Extract base type from formats like "external_with_label_a_1w" -> "external_with_label"
      const baseType = imageType.replace(/_[a-z]_1w$/, "");

      // Configure canvas based on base image type
      switch (baseType) {
        case "external_with_label":
          setShowShutters(true);
          setShowMeasurements(null);
          setShowLabels(false);
          setShowModuleLabels("all");
          setShow3d(false);
          break;

        case "internal_with_height_and_width":
          setShowShutters(false);
          setShowMeasurements("hw");
          setShowLabels(false);
          setShowModuleLabels(null);
          setShow3d(false);
          break;

        default:
          break;
      }

      // If viewConfig is provided, set camera view and wall view
      if (viewConfig) {
        if (viewConfig.cameraView !== undefined) {
          setCameraView(viewConfig.cameraView);
        }
        if (viewConfig.wallView !== undefined) {
          setWallView(viewConfig.wallView);
        }
      }

      // Force update to ensure changes are applied
      forceUpdate();
    },
    [
      showShutters,
      showMeasurements,
      showLabels,
      showModuleLabels,
      show3d,
      cameraView,
      wallView,
      setCameraView,
      setWallView,
      forceUpdate,
    ]
  );

  const restoreOriginalState = useCallback(() => {
    if (originalStateRef.current) {
      setShowShutters(originalStateRef.current.showShutters);
      setShowMeasurements(originalStateRef.current.showMeasurements);
      setShowLabels(originalStateRef.current.showLabels);
      setShowModuleLabels(originalStateRef.current.showModuleLabels);
      setShow3d(originalStateRef.current.show3d);
      if (originalStateRef.current.cameraView !== undefined) {
        setCameraView(originalStateRef.current.cameraView);
      }
      if (originalStateRef.current.wallView !== undefined) {
        setWallView(originalStateRef.current.wallView);
      }
      originalStateRef.current = null;
      forceUpdate();
    }
  }, [forceUpdate, setCameraView, setWallView]);

  // Expose configuration functions to parent
  // Register functions when inside SectionCanvas OR when setCanvasFunctions is provided directly
  useEffect(() => {
    const functions = {
      configureCanvasForImageType,
      restoreOriginalState,
      forceUpdate,
      setCameraView,
      setWallView,
    };

    // If inside SectionCanvas, use the callback
    if (
      isRoomViewInSectionCanvas &&
      typeof onConfigureFunctionsReady === "function"
    ) {
      onConfigureFunctionsReady(functions);
    }

    // If setCanvasFunctions is provided directly (e.g., from Project), register there
    if (setCanvasFunctions && typeof setCanvasFunctions === "function") {
      setCanvasFunctions(functions);
    }
  }, [
    isRoomViewInSectionCanvas,
    configureCanvasForImageType,
    restoreOriginalState,
    forceUpdate,
    onConfigureFunctionsReady,
    setCanvasFunctions,
  ]);

  // Update canvasContainerRef to point to the container with #canvas-div
  // containerRef points to the #canvas-div element itself
  useEffect(() => {
    if (canvasContainerRef && containerRef?.current) {
      canvasContainerRef.current = containerRef.current;
    }
  }, [canvasContainerRef, containerRef]);

  // ########################################################################################

  const toggleInternals = () => {
    if (!showShutters && showModuleLabels === "spaces") {
      setShowModuleLabels(null);
    }
    setShowShutters((prevState) => !prevState);
    setShowRemovedShutters(false);
  };

  const createWall = () => {
    addWall({ projectId: projectId, roomId: currentRoom.id }).then(
      (response) => {
        selectionManager.triggerLoadRoom(currentRoom.id);
      }
    );
  };

  function generateMeasurements() {
    const eligibleObjects = getAllObjects({
      groupChildren: true,
      walls: false,
    });

    const filteredEligibleObjects = eligibleObjects;

    const generator = new AnnotationGenerator(filteredEligibleObjects, {
      cameraView: cameraView,
      groups: currentRoom.groups,
    });

    generator.process();
    setAnnotations(generator.getValidAnnotations());
  }

  // Transform annotation position based on current camera view
  // Uses the same logic as renderBorder for positioning
  const transformAnnotationPosition = (annotation, object) => {
    if (!object) return { x: annotation.position.x, y: annotation.position.y, useRight: false, useTop: false };

    let newLeft = null;
    let newRight = null;
    let newY = annotation.position.y;
    let useRight = false;
    let useTop = false;
    const widthMeasurementGaps = {
      // Width measurement item is ~40px tall; keep a larger offset
      // so the whole annotation stays outside the module.
      module: 56 * renderingSize,
      group: 84 * renderingSize,
      wall: 112 * renderingSize,
    };

    // Calculate offset from object's left edge in front view
    const offsetX = annotation.position.x - (object.position?.x || 0);

    if (isTopView()) {
      // Top view: x stays x, y becomes z (top positioning)
      newLeft = annotation.position.x;
      useTop = true;
    } else if (["module", "group"].includes(object.type)) {
      // Handle modules and groups - same logic as renderBorder
      let rotation = object.rotation || 0;
      let group = null;
      let groupedModule = false;

      if (object.group_id && object.type === "module") {
        group = selectionManager.findGroup(object.group_id);
        if (group) {
          groupedModule = true;
          rotation = group.rotation || rotation;
        }
      }

      if (cameraView === "front") {
        // Front view: use x directly
        newLeft = annotation.position.x;
      } else if (cameraView === "right") {
        // Right view: map x to z
        if (object.type === "group") {
          // For groups: left = item.position.z
          newLeft = object.position.z + offsetX;
        } else {
          // For modules: use computeModulePosition
          const computedRotation = computeRotation(rotation, cameraView);
          const spec = getModulePositionSpec(cameraView, computedRotation, !!group);
          const positionStyle = computeModulePosition(group, object, spec, renderingSize);

          if (positionStyle.left) {
            const objectLeft = pixelParser(positionStyle.left);
            newLeft = objectLeft + offsetX;
          } else {
            newLeft = object.position.z + offsetX;
          }
        }
      } else if (cameraView === "left" || cameraView === "back") {

        let rotation = object.rotation;
        let group = null;

        if (object.group_id && object.type === "module") {
          group = selectionManager.findGroup(object.group_id);
          if (group) {
            rotation = group.rotation;
          }
        }

        const computedRotation = computeRotation(rotation, cameraView);

        const spec = getModulePositionSpec(
          cameraView,
          computedRotation,
          !!group
        );

        const positionStyle = computeModulePosition(
          group,
          object,
          spec,
          renderingSize
        );

        if (annotation.type === "height") {

          if (positionStyle.right !== undefined) {

            const rightEdgePx = pixelParser(positionStyle.right);

            // 🔥 Conditional gap
            const gapPx = object.group_id ? 1300 : 1000;

            newRight = rightEdgePx + gapPx;

            newLeft = null;
            useRight = true;
          }

        } else {

          if (positionStyle.right !== undefined) {
            const rightEdgePx = pixelParser(positionStyle.right);
            newRight = rightEdgePx - offsetX;
            newLeft = null;
            useRight = true;
          }
        }
      }

      // Keep width measurements close to the same item
      // instead of far offsets generated by AnnotationGenerator.
      if (annotation.type !== "height") {
        const objectBottom = object.position?.y || 0;
        const gap = widthMeasurementGaps[object.type] || widthMeasurementGaps.module;
        newY = objectBottom - gap;
      }
    } else {
      // Default: front view
      newLeft = annotation.position.x;
    }

    return {
      x: newLeft !== null ? newLeft : annotation.position.x,
      y: newY,
      right: newRight !== null ? newRight : null,
      useRight,
      useTop,
    };
  };

  // Detect and resolve overlapping measurements
  const resolveMeasurementOverlaps = (annotations) => {
    // First, transform all annotations to get their final positions
    const transformedAnnotations = annotations.map((annotation) => {
      const object = annotation.object;
      const transformed = transformAnnotationPosition(annotation, object);
      // Store both original dimensions (for overlap detection) and rendered dimensions (for styling)
      const originalHeight = annotation.isHeight() ? annotation.dimensions.height : null;
      const originalWidth = !annotation.isHeight() ? annotation.dimensions.width : null;
      const measurementHeight = originalHeight ? originalHeight / renderingSize : null;
      const measurementWidth = originalWidth ? originalWidth / renderingSize : null;

      return {
        annotation,
        transformed,
        originalHeight,
        originalWidth,
        measurementHeight,
        measurementWidth,
      };
    });

    // Group measurements by positioning type and measurement type
    const rightPositioned = transformedAnnotations.filter(
      (t) => t.transformed.useRight && t.transformed.right !== null
    );
    const topPositioned = transformedAnnotations.filter(
      (t) => t.transformed.useTop
    );
    // Left-positioned height measurements (standard case - most common)
    const leftPositionedHeight = transformedAnnotations.filter(
      (t) => !t.transformed.useRight && t.annotation.isHeight()
    );
    // Bottom-positioned width measurements (standard case)
    const bottomPositionedWidth = transformedAnnotations.filter(
      (t) => !t.transformed.useTop && !t.annotation.isHeight()
    );

    // Measurement element width (from annotation_generator.js - size = 200)
    const measurementElementWidth = 200;
    // Minimum spacing between measurements in original coordinate system
    const minSpacing = measurementElementWidth + 50; // Add extra spacing for readability

    // Resolve overlaps for left-positioned height measurements (standard height measurements)
    if (leftPositionedHeight.length > 0) {
      leftPositionedHeight.sort((a, b) => {
        const xDiff = a.transformed.x - b.transformed.x;
        if (Math.abs(xDiff) < 50) {
          return a.transformed.y - b.transformed.y;
        }
        return xDiff;
      });

      leftPositionedHeight.forEach((item, index) => {
        if (index === 0) return;

        const currentTop = item.transformed.y;
        const currentHeight = item.originalHeight || 0;
        const currentLeft = item.transformed.x;
        const currentBottom = currentTop + currentHeight;
        const currentRight = currentLeft + measurementElementWidth;

        // Check all previous items for overlaps
        let adjustedLeft = currentLeft;
        for (let i = 0; i < index; i++) {
          const prevItem = leftPositionedHeight[i];
          const prevTop = prevItem.transformed.y;
          const prevHeight = prevItem.originalHeight || 0;
          const prevBottom = prevTop + prevHeight;
          const prevLeft = prevItem.transformed.x;
          const prevRight = prevLeft + measurementElementWidth;

          const verticalOverlap =
            currentTop < prevBottom && currentBottom > prevTop;

          const horizontalOverlap =
            Math.abs(currentLeft - prevLeft) < 100 ||
            (currentLeft < prevRight && currentRight > prevLeft);

          if (verticalOverlap && horizontalOverlap) {
            adjustedLeft = Math.min(adjustedLeft, prevLeft - minSpacing);
          }
        }

        item.transformed.x = adjustedLeft;
      });
    }

    // Resolve overlaps for right-positioned measurements (height measurements in left/back view)
    if (rightPositioned.length > 0) {
      // Sort by y position first (top to bottom), then by right value
      rightPositioned.sort((a, b) => {
        if (Math.abs(a.transformed.y - b.transformed.y) < 50) {
          return a.transformed.right - b.transformed.right;
        }
        return a.transformed.y - b.transformed.y;
      });

      rightPositioned.forEach((item, index) => {
        if (index === 0) return;

        const currentTop = item.transformed.y;
        const currentHeight = item.originalHeight || 0;
        const currentRight = item.transformed.right;
        const currentBottom = currentTop + currentHeight;
        const currentLeft = currentRight - measurementElementWidth;

        // Check all previous items for overlaps
        let adjustedRight = currentRight;
        for (let i = 0; i < index; i++) {
          const prevItem = rightPositioned[i];
          const prevTop = prevItem.transformed.y;
          const prevHeight = prevItem.originalHeight || 0;
          const prevBottom = prevTop + prevHeight;
          const prevRight = prevItem.transformed.right;
          const prevLeft = prevRight - measurementElementWidth;

          // Check if bounding boxes overlap
          // Vertical overlap: measurements overlap in y-axis
          const verticalOverlap =
            currentTop < prevBottom && currentBottom > prevTop;

          // Horizontal overlap: measurements overlap in x-axis (considering measurement width)
          const horizontalOverlap =
            currentLeft < prevRight && currentRight > prevLeft;

          // If both vertical and horizontal overlap, need to adjust
          if (verticalOverlap && horizontalOverlap) {
            // Move this measurement further to the right (smaller right value)
            adjustedRight = Math.min(adjustedRight, prevRight - minSpacing);
          }
        }

        item.transformed.right = adjustedRight;
      });
    }

    // Resolve overlaps for bottom-positioned width measurements (standard width measurements)
    if (bottomPositionedWidth.length > 0) {
      // Sort by x position first, then by y position
      bottomPositionedWidth.sort((a, b) => {
        if (Math.abs(a.transformed.x - b.transformed.x) < 50) {
          return a.transformed.y - b.transformed.y;
        }
        return a.transformed.x - b.transformed.x;
      });

      bottomPositionedWidth.forEach((item, index) => {
        if (index === 0) return;

        const currentLeft = item.transformed.x;
        const currentWidth = item.originalWidth || 0;
        const currentY = item.transformed.y;
        const currentRight = currentLeft + currentWidth;
        // For bottom positioning: y is distance from bottom, measurement extends upward
        // Top edge of measurement = y - measurementElementWidth, bottom edge = y
        const currentTop = currentY - measurementElementWidth;
        const currentBottom = currentY;

        // Check all previous items for overlaps
        let adjustedY = currentY;
        for (let i = 0; i < index; i++) {
          const prevItem = bottomPositionedWidth[i];
          const prevLeft = prevItem.transformed.x;
          const prevWidth = prevItem.originalWidth || 0;
          const prevRight = prevLeft + prevWidth;
          const prevY = prevItem.transformed.y;
          const prevTop = prevY - measurementElementWidth;
          const prevBottom = prevY;

          // Check if bounding boxes overlap
          // Horizontal overlap: measurements overlap in x-axis
          const horizontalOverlap =
            currentLeft < prevRight && currentRight > prevLeft;

          // Vertical overlap: measurements overlap in y-axis (considering measurement height)
          const verticalOverlap =
            currentTop < prevBottom && currentBottom > prevTop;

          // If both horizontal and vertical overlap, need to adjust
          if (horizontalOverlap && verticalOverlap) {
            // Move this measurement further down (larger y value for bottom positioning)
            adjustedY = Math.max(adjustedY, prevY + minSpacing);
          }
        }

        item.transformed.y = adjustedY;
      });
    }

    // Resolve overlaps for top-positioned measurements (width measurements in top view)
    if (topPositioned.length > 0) {
      // Sort by x position first, then by y value
      topPositioned.sort((a, b) => {
        if (Math.abs(a.transformed.x - b.transformed.x) < 50) {
          return a.transformed.y - b.transformed.y;
        }
        return a.transformed.x - b.transformed.x;
      });

      topPositioned.forEach((item, index) => {
        if (index === 0) return;

        const currentLeft = item.transformed.x;
        const currentWidth = item.originalWidth || 0;
        const currentTop = item.transformed.y;
        const currentRight = currentLeft + currentWidth;
        const currentBottom = currentTop + measurementElementWidth; // For top positioning, measurement extends downward

        // Check all previous items for overlaps
        let adjustedTop = currentTop;
        for (let i = 0; i < index; i++) {
          const prevItem = topPositioned[i];
          const prevLeft = prevItem.transformed.x;
          const prevWidth = prevItem.originalWidth || 0;
          const prevRight = prevLeft + prevWidth;
          const prevTop = prevItem.transformed.y;
          const prevBottom = prevTop + measurementElementWidth;

          // Check if bounding boxes overlap
          // Horizontal overlap: measurements overlap in x-axis
          const horizontalOverlap =
            currentLeft < prevRight && currentRight > prevLeft;

          // Vertical overlap: measurements overlap in y-axis (considering measurement height)
          const verticalOverlap =
            currentTop < prevBottom && currentBottom > prevTop;

          // If both horizontal and vertical overlap, need to adjust
          if (horizontalOverlap && verticalOverlap) {
            // Move this measurement further down (larger top value)
            adjustedTop = Math.max(adjustedTop, prevTop + minSpacing);
          }
        }

        item.transformed.y = adjustedTop;
      });
    }

    return transformedAnnotations;
  };

  // TODO. Move to a component.
  function renderMeasurements() {
    // Keep measurements anchored to their own item.
    // Overlap resolution can shift labels near neighbouring items.
    const visibleAnnotations = annotations.filter(
      (annotation) => annotation?.object?.type !== "group"
    );

    const resolvedAnnotations = visibleAnnotations.map((annotation) => {
      const transformed = transformAnnotationPosition(annotation, annotation.object);
      const originalHeight = annotation.isHeight()
        ? annotation.dimensions.height
        : null;
      const originalWidth = !annotation.isHeight()
        ? annotation.dimensions.width
        : null;

      return {
        annotation,
        transformed,
        measurementHeight: originalHeight ? originalHeight / renderingSize : null,
        measurementWidth: originalWidth ? originalWidth / renderingSize : null,
      };
    });

    const horizontalStart = (item) => {
      if (item.transformed.useRight && item.transformed.right !== null) {
        return -item.transformed.right;
      }
      return item.transformed.x;
    };

    const orderedAnnotations = [...resolvedAnnotations].sort((a, b) => {
      const aIsWidth = !a.annotation.isHeight();
      const bIsWidth = !b.annotation.isHeight();
      if (aIsWidth && bIsWidth) {
        const yDelta = Math.abs(a.transformed.y - b.transformed.y);
        if (yDelta < 120 * renderingSize) {
          return horizontalStart(a) - horizontalStart(b);
        }
      }
      return 0;
    });

    // Keep width measurements on separate lanes when they overlap,
    // so one segment's marker/value doesn't visually cut another.
    const laneAdjustedAnnotations = [...orderedAnnotations];
    const widthLaneGap = 44 * renderingSize;
    const widthItems = laneAdjustedAnnotations
      .filter((item) => !item.annotation.isHeight())
      .sort((a, b) => {
        // Process smaller measurements first (module segments),
        // then push wider totals to outer lanes.
        const aWidth = a.annotation.dimensions?.width || 0;
        const bWidth = b.annotation.dimensions?.width || 0;
        if (aWidth !== bWidth) return aWidth - bWidth;
        return horizontalStart(a) - horizontalStart(b);
      });

    widthItems.forEach((current, currentIndex) => {
      const currentLeft = horizontalStart(current);
      const currentWidth = current.annotation.dimensions?.width || 0;
      const currentRight = currentLeft + currentWidth;

      for (let i = 0; i < currentIndex; i++) {
        const prev = widthItems[i];
        const prevLeft = horizontalStart(prev);
        const prevWidth = prev.annotation.dimensions?.width || 0;
        const prevRight = prevLeft + prevWidth;
        const sameSide = !!current.transformed.useTop === !!prev.transformed.useTop;
        if (!sameSide) continue;

        const overlapsHorizontally = currentLeft < prevRight && currentRight > prevLeft;
        const tooCloseVertically =
          Math.abs(current.transformed.y - prev.transformed.y) < widthLaneGap;
        if (!overlapsHorizontally || !tooCloseVertically) continue;

        if (current.transformed.useTop) {
          current.transformed.y = prev.transformed.y + widthLaneGap;
        } else {
          current.transformed.y = prev.transformed.y - widthLaneGap;
        }
      }
    });

    return (
      <div className="measurements" style={{ position: "relative", zIndex: zIndexMax + 10000 }}>
        {laneAdjustedAnnotations.map(({ annotation, transformed, measurementHeight, measurementWidth }, renderIndex) => {
          const isWidth = !annotation.isHeight();
          const objectBottom = annotation.object?.position?.y ?? 0;
          const isWidthBelowObject =
            isWidth && !transformed.useTop && transformed.y <= objectBottom;
          const widthOrderClass = isWidth
            ? isWidthBelowObject
              ? "width-marker-near"
              : "width-marker-far"
            : "";
          // Later render items should not cover earlier adjacent values.
          const widthOrderBoost = isWidth ? Math.max(0, 1000 - renderIndex) : 0;
          const style = {
            position: "absolute",
            zIndex: zIndexMax + 10000 + widthOrderBoost,
          };

          // Set horizontal position
          if (transformed.useRight && transformed.right !== null) {
            style.right = `${transformed.right / renderingSize}px`;
          } else {
            style.left = `${transformed.x / renderingSize}px`;
          }

          // Set vertical position
          if (transformed.useTop) {
            style.top = `${transformed.y / renderingSize}px`;
          } else {
            style.bottom = `${transformed.y / renderingSize}px`;
          }

          if (annotation.isHeight()) {
            style.height = `${measurementHeight}px`;
          } else {
            style.width = `${measurementWidth}px`;
          }

          return (
            <div
              className={`measurement-item ${
                annotation.isHeight() && "height"
              } ${annotation.isAlt && "flipped"} ${
                !annotation.isHeight() && "width"
              } ${widthOrderClass}`}
              style={style}
            >
              {annotation.isHeight() ? (
                // Use SVG for vertical text (height measurements) - html2canvas supports SVG correctly
                <svg
                  className="text"
                  width="40"
                  height={measurementHeight}
                  viewBox={`0 0 40 ${measurementHeight}`}
                  preserveAspectRatio="none"
                  style={{
                    flexGrow: 1,
                    height: "100%",
                    display: "block",
                  }}
                >
                  <text
                    x="20"
                    y={measurementHeight / 2}
                    writingMode="vertical-rl"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="20"
                    fill="currentColor"
                    textOrientation="upright"
                    transform={`rotate(180, 20, ${measurementHeight / 2})`}
                  >
                    {String(annotation.text)}
                  </text>
                </svg>
              ) : (
                <div className="text">{annotation.text}</div>
              )}
              <div className="marker">
                <Arrow direction={annotation.isHeight() ? "up" : "left"} />
                <div className="line" />
                <Arrow direction={annotation.isHeight() ? "down" : "right"} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderDepthMeasurements(module, group = null) {
    let rotation = module.rotation;
    if (rotation === 0 && group) rotation = group.rotation;
    const computedRotation = computeRotation(rotation, cameraView);

    let style = {
      textAlign: "center",
      position: "absolute",
      transform: "translateX(-50%)",
      zIndex: zIndexMax + 30000,
      pointerEvents: "none",
      isolation: "isolate",
    };
    const depthLabelOffset = 320 / renderingSize;

    if (isTopView()) {
      let x = module.position.x / renderingSize;
      let z = (module.position.z || 0) / renderingSize;

      if (group) {
        if (rotation === 90) {
          const groupFrontZ = group.position.z + group.dimensions.depth;
          const moduleFrontZ = module.position.z + module.dimensions.depth;
          x = (group.position.x + (groupFrontZ - moduleFrontZ)) / renderingSize;
          z = (group.position.z - (group.position.x - module.position.x)) / renderingSize;
        } else if (rotation === 180) {
          const groupX2 = group.position.x + group.dimensions.width;
          const moduleX2 = module.position.x + module.dimensions.width;
          x = (group.position.x + (groupX2 - moduleX2)) / renderingSize;
          const groupFrontZ = group.position.z + group.dimensions.depth;
          const moduleFrontZ = module.position.z + module.dimensions.depth;
          z = (group.position.z + (groupFrontZ - moduleFrontZ)) / renderingSize;
        } else if (rotation === 270) {
          x = (group.position.x - (group.position.z - module.position.z)) / renderingSize;
          const groupX2 = group.position.x + group.dimensions.width;
          const moduleX2 = module.position.x + module.dimensions.width;
          z = (group.position.z + (groupX2 - moduleX2)) / renderingSize;
        }
      }

      // Calculate module dimensions in top view
      let moduleWidth, moduleHeight;
      if ([90, 270].includes(rotation)) {
        moduleWidth = module.dimensions.depth / renderingSize;
        moduleHeight = module.dimensions.width / renderingSize;
      } else {
        moduleWidth = module.dimensions.width / renderingSize;
        moduleHeight = module.dimensions.depth / renderingSize;
      }

      const centerX = x + (moduleWidth / 2);
      // Position measurement below the module (z + moduleHeight + offset)
      const measurementTop = z + moduleHeight + depthLabelOffset;

      style.left = `${centerX}px`;
      style.top = `${measurementTop}px`;
      style.transform = "translateX(-50%)";
    } else {
      // Use the same positioning logic as renderAllLabels (getModulePos)
      const spec = getModulePositionSpec(cameraView, computedRotation, !!group);
      const position = computeModulePosition(group, module, spec, renderingSize);
      const pixelParser = (str) => parseFloat(str.replace("px", "")) || 0;

      const width = [90, 270].includes(computedRotation)
        ? (module.dimensions.depth / renderingSize)
        : (module.dimensions.width / renderingSize);
      const height = [90, 270].includes(computedRotation)
        ? (module.dimensions.width / renderingSize)
        : (module.dimensions.height / renderingSize);

      // Initialize with default positions based on view
      let left, bottom;
      if (cameraView === "right") {
        left = module.position.z / renderingSize;
        bottom = module.position.y / renderingSize;
      } else if (cameraView === "left") {
        // For left view, horizontal is z-axis
        left = module.position.z / renderingSize;
        bottom = module.position.y / renderingSize;
      } else if (cameraView === "back") {
        // For back view, horizontal is x-axis (inverted)
        left = module.position.x / renderingSize;
        bottom = module.position.y / renderingSize;
      } else {
        // front view
        left = module.position.x / renderingSize;
        bottom = module.position.y / renderingSize;
      }

      if (position.left !== undefined) {
        left = pixelParser(position.left);
      } else if (position.right !== undefined) {
        const right = pixelParser(position.right);
        if (cameraView === "left" || cameraView === "back") {
          let containerWidth = 0;
          if (currentRoom.walls && currentRoom.walls.length > 0) {
            if (cameraView === "left") {
              // For left view, container width is based on z-axis (depth)
              containerWidth = Math.max(...currentRoom.walls.map(w => {
                const wallZ = w.position.z || 0;
                const wallDepth = w.dimensions.depth || 0;
                return (wallZ + wallDepth) / renderingSize;
              }));
            } else {
              // For back view, container width is based on x-axis (width)
              containerWidth = Math.max(...currentRoom.walls.map(w => (w.position.x + w.dimensions.width) / renderingSize));
            }
          } else if (currentRoom.dimensions) {
            if (cameraView === "left" && currentRoom.dimensions.depth) {
              containerWidth = currentRoom.dimensions.depth / renderingSize;
            } else if (cameraView === "back" && currentRoom.dimensions.width) {
              containerWidth = currentRoom.dimensions.width / renderingSize;
            }
          }
          if (containerWidth > 0) {
            left = containerWidth - right - width;
          }
          // If containerWidth is 0, left already has the correct fallback value
        } else {
          left = right - width;
        }
      }

      if (position.top !== undefined) {
        bottom = pixelParser(position.top);
      } else if (position.bottom !== undefined) {
        bottom = pixelParser(position.bottom);
      }

      const moduleLeft = left;
      const moduleRight = left + width;
      const moduleBottom = bottom;
      const moduleTop = bottom + height;

      const chooseDepthLabelY = () => {
        const labelGapPx = 8;
        const allowedRotations = getRotationsForWallView(cameraView, wallView);
        const visibleModules = [
          ...currentRoom.groups
            .filter((g) => allowedRotations.includes(g.rotation))
            .flatMap((g) => (g.modules || []).map((m) => ({ module: m, group: g }))),
          ...(currentRoom.modules || [])
            .filter((m) => allowedRotations.includes(m.rotation))
            .map((m) => ({ module: m, group: null })),
        ];

        let nearestAbove = Number.POSITIVE_INFINITY;
        let nearestBelow = Number.POSITIVE_INFINITY;

        visibleModules.forEach(({ module: candidate, group: candidateGroup }) => {
          if (candidate?.id === module?.id) return;

          let candidateRotation = candidate.rotation;
          if (candidateRotation === 0 && candidateGroup) {
            candidateRotation = candidateGroup.rotation;
          }
          const candidateComputedRotation = computeRotation(
            candidateRotation,
            cameraView
          );
          const candidateSpec = getModulePositionSpec(
            cameraView,
            candidateComputedRotation,
            !!candidateGroup
          );
          const candidatePosition = computeModulePosition(
            candidateGroup,
            candidate,
            candidateSpec,
            renderingSize
          );

          const candidateWidth = [90, 270].includes(candidateComputedRotation)
            ? candidate.dimensions.depth / renderingSize
            : candidate.dimensions.width / renderingSize;
          const candidateHeight = [90, 270].includes(candidateComputedRotation)
            ? candidate.dimensions.width / renderingSize
            : candidate.dimensions.height / renderingSize;

          let candidateLeft = candidate.position.x / renderingSize;
          let candidateBottom = candidate.position.y / renderingSize;

          if (candidatePosition.left !== undefined) {
            candidateLeft = pixelParser(candidatePosition.left);
          } else if (candidatePosition.right !== undefined) {
            const right = pixelParser(candidatePosition.right);
            if (cameraView === "left" || cameraView === "back") {
              let containerWidth = 0;
              if (currentRoom.walls && currentRoom.walls.length > 0) {
                if (cameraView === "left") {
                  containerWidth = Math.max(
                    ...currentRoom.walls.map((w) => {
                      const wallZ = w.position.z || 0;
                      const wallDepth = w.dimensions.depth || 0;
                      return (wallZ + wallDepth) / renderingSize;
                    })
                  );
                } else {
                  containerWidth = Math.max(
                    ...currentRoom.walls.map(
                      (w) => (w.position.x + w.dimensions.width) / renderingSize
                    )
                  );
                }
              } else if (currentRoom.dimensions) {
                if (cameraView === "left" && currentRoom.dimensions.depth) {
                  containerWidth = currentRoom.dimensions.depth / renderingSize;
                } else if (
                  cameraView === "back" &&
                  currentRoom.dimensions.width
                ) {
                  containerWidth = currentRoom.dimensions.width / renderingSize;
                }
              }
              if (containerWidth > 0) {
                candidateLeft = containerWidth - right - candidateWidth;
              }
            } else {
              candidateLeft = right - candidateWidth;
            }
          }

          if (candidatePosition.top !== undefined) {
            candidateBottom = pixelParser(candidatePosition.top);
          } else if (candidatePosition.bottom !== undefined) {
            candidateBottom = pixelParser(candidatePosition.bottom);
          }

          const candidateRight = candidateLeft + candidateWidth;
          const overlapsHorizontally =
            moduleLeft < candidateRight && moduleRight > candidateLeft;

          if (!overlapsHorizontally) return;

          const candidateTop = candidateBottom + candidateHeight;
          if (candidateBottom >= moduleTop) {
            nearestAbove = Math.min(nearestAbove, candidateBottom - moduleTop);
          } else if (candidateTop <= moduleBottom) {
            nearestBelow = Math.min(nearestBelow, moduleBottom - candidateTop);
          }
        });

        const hasMoreSpaceAbove = nearestAbove > nearestBelow;
        return hasMoreSpaceAbove ? moduleTop + labelGapPx : moduleBottom - labelGapPx;
      };

      const measurementY = chooseDepthLabelY();

      // For left and back views, use right positioning like labels do
      if (cameraView === "left" || cameraView === "back") {
        let moduleRight = position.right !== undefined ? pixelParser(position.right) : null;

        // If right is not available, calculate it from left and container width
        if (moduleRight === null) {
          let containerWidth = 0;
          if (currentRoom.walls && currentRoom.walls.length > 0) {
            if (cameraView === "left") {
              containerWidth = Math.max(...currentRoom.walls.map(w => {
                const wallZ = w.position.z || 0;
                const wallDepth = w.dimensions.depth || 0;
                return (wallZ + wallDepth) / renderingSize;
              }));
            } else {
              containerWidth = Math.max(...currentRoom.walls.map(w => (w.position.x + w.dimensions.width) / renderingSize));
            }
          } else if (currentRoom.dimensions) {
            if (cameraView === "left" && currentRoom.dimensions.depth) {
              containerWidth = currentRoom.dimensions.depth / renderingSize;
            } else if (cameraView === "back" && currentRoom.dimensions.width) {
              containerWidth = currentRoom.dimensions.width / renderingSize;
            }
          }
          if (containerWidth > 0) {
            // Calculate right from left: right = containerWidth - left - width
            moduleRight = containerWidth - left - width;
          }
        }

        if (moduleRight !== null) {
          // Calculate module center relative to right edge
          // Module center from right = moduleRight + (width / 2)
          const moduleCenterFromRight = moduleRight + (width / 2);

          // Position measurement: center is at moduleCenterFromRight from right
          // Using translateX(50%) to center the measurement on that point
          style.right = `${moduleCenterFromRight}px`;
          style.transform = "translateX(50%)";
          style.bottom = `${bottom - depthLabelOffset}px`;
        } else {
          // Final fallback to left positioning
          const moduleCenterX = left + (width / 2);
          style.left = `${moduleCenterX}px`;
          style.transform = "translateX(-50%)";
          style.bottom = `${bottom - depthLabelOffset}px`;
        }
      } else {
        // For front and right views, use left positioning
        const moduleCenterX = left + (width / 2);
        style.left = `${moduleCenterX}px`;
        style.transform = "translateX(-50%)";
        style.bottom = `${bottom - depthLabelOffset}px`;
      }
    }

    return (
      <>
        <div
          className="measurement-depth position-absolute px-3"
          style={style}
        >
          {module.dimensions.depth}{" "}
        </div>
      </>
    );
  }

  function getOutlineForItem(type) {
    if (type == "module" || type == "wall") {
      return "#0000CC";
    } else if (type == "group") {
      return "#CC0000";
    } else {
      return "#00AA00";
    }
  }

  const pixelParser = (str) => parseInt(str.replace("px", "")) * renderingSize;

  function renderBorder(item, mode) {
    let position = item.position;
    let dimensions = item.dimensions;
    let outlineColor = getOutlineForItem(item.type);

    let newWidth = null;
    let newHeight = null;
    let newLeft = null;
    let newRight = null;
    let newTop = null;
    const style = {};

    // Handle modules and groups with rotation
    if (["module", "group"].includes(item.type)) {
      let rotation = item.rotation;
      let group = null;
      let groupedModule = false;

      if (item.group_id) {
        group = selectionManager.findGroup(item.group_id);
        groupedModule = true;
        rotation = group.rotation;
      }

      if (isTopView()) {
        // Calculate left position when modules grouped
        if (groupedModule) {
          if (rotation === 90) {
            const groupFrontZ = group.position.z + group.dimensions.depth;
            const moduleFrontZ = item.position.z + item.dimensions.depth;

            newLeft = group.position.x + (groupFrontZ - moduleFrontZ);
          } else if (rotation === 180) {
            const groupX2 = group.position.x + group.dimensions.width;
            const moduleX2 = item.position.x + item.dimensions.width;

            newLeft = group.position.x + (groupX2 - moduleX2);
          } else if (rotation === 270) {
            newLeft = group.position.x - (group.position.z - item.position.z);
          }
        }

        // Calculate top position for groups and modules
        if (rotation === 90 && groupedModule) {
          newTop = group.position.z - (group.position.x - item.position.x);
        } else if (rotation === 180 && groupedModule) {
          const groupFrontZ = group.position.z + group.dimensions.depth;
          const moduleFrontZ = item.position.z + item.dimensions.depth;

          newTop = group.position.z + (groupFrontZ - moduleFrontZ);
        } else if (rotation === 270 && groupedModule) {
          const groupX2 = group.position.x + group.dimensions.width;
          const moduleX2 = item.position.x + item.dimensions.width;

          newTop = group.position.z + (groupX2 - moduleX2);
        } else {
          newTop = item.position.z;
        }

        // Set height and width for groups and modules based on side view
        if ([90, 270].includes(rotation)) {
          newWidth = item.dimensions.depth;
          newHeight = item.dimensions.width;
        } else {
          newHeight = item.dimensions.depth;
        }
      } else {
        if (item.type === "group") {
          const computedRotation = computeRotation(rotation, cameraView);

          if ([90, 270].includes(computedRotation))
            newWidth = item.dimensions.depth;

          if (cameraView === "right") {
            newLeft = item.position.z;
          } else if (cameraView === "left") {
            newLeft = "auto";
            newRight = item.position.z;
          } else if (cameraView === "back") {
            newLeft = "auto";
            newRight = item.position.x;
          }
        } else {
          const computedRotation = computeRotation(rotation, cameraView);

          const spec = getModulePositionSpec(
            cameraView,
            computedRotation,
            !!group
          );

          const positionStyle = computeModulePosition(
            group,
            item,
            spec,
            renderingSize
          );

          if ([90, 270].includes(computedRotation)) {
            newWidth = item.dimensions.depth;
          }

          if (positionStyle.left) {
            if (positionStyle.left === "auto") newLeft = "auto";
            else newLeft = pixelParser(positionStyle.left);
          }

          if (positionStyle.right) newRight = pixelParser(positionStyle.right);
        }
      }
    } else if (["shutter", "partition", "space"].includes(item.type)) {
      const module = selectionManager.findModule(item.order_item_id);
      let group = null;
      let rotation = module.rotation;

      const objectToTransform = item.type === "space" ? item : item.component;

      if (module.group_id) {
        group = selectionManager.findGroup(module.group_id);
        rotation = group.rotation;
      }

      const computedRotation = computeRotation(rotation, cameraView);

      let container = null;

      if (item.type === "space" && item.path !== "") {
        let parentSpacePath = getParentSpacePath(item.path);

        container = selectionManager.findSpace(parentSpacePath, module.id);
      } else {
        container = module;
      }

      const transform = applyTransform(
        objectToTransform,
        container,
        { rotYDeg: computedRotation, cameraView },
        renderingSize
      );

      const relPosition =
        item.type === "space" ? item.abs_position : item.component.position;

      const spec = getModulePositionSpec(cameraView, computedRotation, !!group);

      const calculatedPosition = computeModulePosition(
        group,
        module,
        spec,
        renderingSize
      );

      if (cameraView === "front") {
        newLeft = module.position.x + relPosition.x;
      } else if (cameraView === "right") {
        newLeft = pixelParser(calculatedPosition.left) + relPosition.x;
      } else if (cameraView === "left") {
        newLeft = "auto";
        // Convert left position to right position
        const left = pixelParser(transform.style.left);
        const containerWidth = container.dimensions.width;
        const right =
          containerWidth + (relPosition.x - left) - left - dimensions.width;

        newRight = pixelParser(calculatedPosition.right) + right;
      } else if (cameraView === "back") {
        newLeft = "auto";
        // Convert left position to right position
        const left = pixelParser(transform.style.left);
        const containerWidth = container.dimensions.width;
        const right =
          containerWidth + (relPosition.x - left) - left - dimensions.width;

        newRight = pixelParser(calculatedPosition.right) + right;
      }

      position = { y: module.position.y + relPosition.y };
    }

    const outlineWidth = mode === "hover" ? 2 : 3;
    const className = mode === "hover" ? "hover-border" : "selection";

    if (newLeft === "auto") style["left"] = "auto";
    else {
      style["left"] = `${
        (newLeft !== null ? newLeft : position.x) / renderingSize
      }px`;
    }

    if (newRight !== null) style["right"] = `${newRight / renderingSize}px`;

    style["height"] = `${
      (newHeight !== null ? newHeight : dimensions.height) / renderingSize
    }px`;

    style["width"] = `${
      (newWidth !== null ? newWidth : dimensions.width) / renderingSize
    }px`;

    if (isTopView()) {
      style["top"] = `${(newTop !== null ? newTop : "") / renderingSize}px`;
    } else style["bottom"] = `${position.y / renderingSize}px`;

    return (
      <div
        className={className}
        style={{
          outline: `${outlineWidth / zoom}px solid ${outlineColor}`,
          ...style,
        }}
      ></div>
    );
  }

  function renderSelectionBorders() {
    const selectionItems = selectionManager.selections();

    if (!selectionItems.length) return;

    return (
      <div className="selection-borders">
        {selectionItems.map((item) =>
          renderBorder(item, "selection", selectionItems)
        )}
      </div>
    );
  }

  function renderHoverBorder() {
    if (!hoverItem || !selectionManager.selections()) {
      return;
    }

    // Don't hover the contextual parent since it will show double border.
    if (
      selectionManager.activeModule() === hoverItem ||
      selectionManager.activeGroup() === hoverItem
    )
      return;

    return renderBorder(hoverItem, "hover");
  }

  return !currentRoom ? (
    <div className="position-relative">
      <CustomizationSpinnerContainer top="200px" left="40%">
        <Spinner />
      </CustomizationSpinnerContainer>
    </div>
  ) : (
    <>
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}
      {canvasLoading && (
        <CustomizationSpinnerContainer top="150px">
          <Spinner />
        </CustomizationSpinnerContainer>
      )}
      <div className="canvas-actions ms-3">
        <div className="toggles">
          <div
            id="toggle-internals"
            className={`toggle-item ${!showShutters && "active"}`}
            onClick={() => toggleInternals()}
          >
            <InternalIcon></InternalIcon>
          </div>
          <UncontrolledTooltip target={`toggle-internals`} placement="bottom">
            Internals (I)
          </UncontrolledTooltip>
          <div
            id="toggle-measurements"
            className={`toggle-item ${
              showMeasurements && "active"
            } position-relative`}
            onClick={handleMeasurementClick}
          >
            <MeasurementIcon></MeasurementIcon>
            {showMeasurements !== null && (
              <div className="mode">{getMeasurementText()}</div>
            )}
          </div>
          <UncontrolledTooltip target={`toggle-measurements`} placement="top">
            Measurements (M)
          </UncontrolledTooltip>
          <div
            id="toggle-removed-shutters"
            className={`toggle-item ${showRemovedShutters && "active"} ${
              !showShutters && "disabled"
            }`}
            onClick={() => setShowRemovedShutters(!showRemovedShutters)}
          >
            <i className="fa fa-eye"></i>
          </div>
          <UncontrolledTooltip
            target={`toggle-removed-shutters`}
            placement="bottom"
          >
            Toggle removed shutters
          </UncontrolledTooltip>
          <div
            id="toggle-labels"
            className={`toggle-item ${
              showModuleLabels && "active"
            } position-relative`}
            onClick={handleLabelClick}
          >
            <i className="fa-solid fa-tags"></i>
            {showModuleLabels !== null && (
              <div className="mode">{getLabelText()}</div>
            )}
          </div>
          <UncontrolledTooltip target={`toggle-labels`} placement="top">
            Labels
          </UncontrolledTooltip>
          <div
            id="toggle-colors"
            className={`toggle-item ${showColors && "active"}`}
            onClick={() => setShowColors(!showColors)}
          >
            <i className="fa-solid fa-palette"></i>{" "}
          </div>
          <UncontrolledTooltip target={`toggle-colors`} placement="bottom">
            Colors
          </UncontrolledTooltip>
          {show3d && access.view_drilling && (
            <>
              <div
                id="toggle-colors"
                className={`toggle-item ${showDrilling && "active"}`}
                onClick={() => setShowDrilling(!showDrilling)}
              >
                <i className="fa-solid fa-screwdriver"></i>{" "}
              </div>
              <UncontrolledTooltip target={`toggle-colors`} placement="bottom">
                Drilling
              </UncontrolledTooltip>
            </>
          )}
          {show3d && (
            <>
              <div
                id="toggle-colors"
                className={`toggle-item ${showEdges && "active"}`}
                onClick={() => setShowEdges(!showEdges)}
              >
                <i className="fas fa-border-style"></i>{" "}
              </div>
              <UncontrolledTooltip target={`toggle-colors`} placement="bottom">
                Show Edges
              </UncontrolledTooltip>
            </>
          )}
          {isTopView() &&
            selectionManagerRef.current
              .selections()
              .some(
                (item) =>
                  (item.type === "module" && !item.is_packed) ||
                  item.type === "group"
              ) && (
              <>
                <div
                  id="rotate-module"
                  className="toggle-item"
                  onClick={handleRotateModule}
                >
                  <i className="fas fa-rotate-right"></i>{" "}
                </div>
                <UncontrolledTooltip
                  target={`rotate-module`}
                  placement="bottom"
                >
                  Rotate
                </UncontrolledTooltip>
              </>
            )}
        </div>
        <div className="toggle-container ms-5 d-flex">
          {cameraViews.map(({ view, label }) => (
            <div
              key={view}
              className={`toggle-button ${cameraView === view && "active"}`}
              onClick={() => setCameraView(view)}
            >
              {label}
            </div>
          ))}
        </div>

        <div className="toggle-container ms-5 d-flex">
          {["1", "3", "4"].map((label) => {
            const isDisabled = isTopView() && label !== "1";

            return (
              <div
                key={label}
                className={`toggle-button ${wallView === label && "active"} ${
                  isDisabled && "disabled"
                }`}
                onClick={() => !isDisabled && setWallView(label)}
                style={{
                  ...(isDisabled && {
                    color: "#999",
                    cursor: "not-allowed",
                  }),
                }}
              >
                {label + "W"}
              </div>
            );
          })}
        </div>
        {access.customize_module && !isRoomViewInSectionCanvas && (
          <div className="actions">
            <>
              <div
                id="add-module"
                className="action-item"
                onClick={() => {
                  selectionManager.clear();
                  setShowPositionModal(false);
                  setHoverItem(null);
                  setCurrentTab("catalog");
                }}
              >
                <i className="fa-solid fa-plus-circle" />
              </div>
              <UncontrolledTooltip target={`add-module`} placement="bottom">
                Add module
              </UncontrolledTooltip>
            </>
            <div
              id="add-wall"
              className="action-item"
              onClick={() => setWallConfirmationOpen(true)}
            >
              <i className="fa-regular fa-square"></i>
            </div>
            <UncontrolledTooltip target={`add-wall`} placement="bottom">
              Add wall
            </UncontrolledTooltip>
          </div>
        )}
      </div>
      {selectionManager.selections().length > 1 && (
        <div
          className="position-absolute"
          style={{
            top: "0.5rem",
            right: "1rem",
            zIndex: 1000,
          }}
        >
          <div
            ref={positionModalRef}
            className="px-3 py-2 fw-bold pointer d-flex align-items-center justify-content-center"
            onClick={() => setShowPositionModal(!showPositionModal)}
            style={{
              backgroundColor: showPositionModal ? "#2c92e6" : "#f8f9fa",
              color: showPositionModal ? "white" : "#333",
              border: "1px solid #CCCCCC",
              borderRadius: "4px",
              fontSize: "0.875rem",
              cursor: "pointer",
              minWidth: "90px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            Position
          </div>
          <RightSideModal
            showModal={showPositionModal}
            setShowModal={setShowPositionModal}
            selectionManager={selectionManager}
            getObjectKey={getObjectKey}
            items={selectionManager.selections()}
            historyManager={historyManager}
            projectId={projectId}
            currentRoom={currentRoom}
            loadRoom={loadRoom}
            targetRef={positionModalRef}
            forceUpdate={forceUpdate}
            cameraView={cameraView}
          />
        </div>
      )}
      <div
        className="position-absolute z-3"
        style={{ bottom: "9rem", right: "1rem" }}
      >
        <div className="toggle-container">
          <div
            className={`toggle-button ${!show3d && "active"}`}
            onClick={() => {
              setShow3d(false);
              setCanvasMode("");
            }}
          >
            2D
          </div>
          <div
            className={`toggle-button ${show3d && "active"}`}
            onClick={() => {
              setShow3d(true);
              setCanvasMode("rotate");
            }}
          >
            3D
          </div>
        </div>
      </div>
      {!show3d ? (
        <div
          className={`fh position-relative d-flex justify-content-center align-items-center overflow-hidden room-canvas-container ${
            canvasLoading || isLoading ? "blurred" : ""
          }`}
          id="canvas-div"
          ref={containerRef}
          tabIndex="0"
          style={{
            cursor: "default",
          }}
          onMouseDown={(event) => clickHandler(event, { type: "room" })}
        >
          <div
            className={`position-absolute d-flex justify-content-left ${
              isCentering && "canvas-container-centering"
            }`}
            style={{
              left: `${position.x}px`,
              bottom: `${position.y}px`,
              transform: `scale(${zoom}) translateZ(0)`,
              overflow: "visible", // Allow labels to extend beyond boundaries
            }}
          >
            {renderRoom()}
            {renderAllLabels()}
            {showMeasurements == "hw" && renderMeasurements()}
            {renderSelectionBorders()}
            {renderHoverBorder()}
          </div>
          {renderDragSelection()}
        </div>
      ) : (
        <div className="canvas-wrapper">
          <ThreeDCanvas
            currentRoom={currentRoom}
            showShutters={showShutters}
            showRemovedShutters={showRemovedShutters}
            canvasMode={canvasMode}
            showDrilling={showDrilling}
            showEdges={showEdges}
            selectionManager={selectionManager}
            mode="canvas"
          ></ThreeDCanvas>
        </div>
      )}
      <ConfirmationModal
        isOpen={wallConfirmationOpen}
        toggle={togglewallConfirmationOpen}
        onConfirm={() => createWall()}
        message="Are you sure you want to add a new Wall?"
        headingText="Add Wall"
      />
    </>
  );
}

export default RoomCanvas;
